/* ==========================================================================
   OTF Facelift — fluid-motion.js
   The motion system: Apple-parameterised springs (damping ratio + response),
   momentum projection, rubber-banding, velocity handoff, interruption.
   New in this build: ambient light that follows the rail, velocity tilt,
   in-card parallax, the add-to-bag flight, sheet stacking, load
   choreography, and the live 9pm countdown.
   Real store: products come from the DOM (Liquid), cart is Shopify's
   /cart js API. Nothing here touches checkout.
   ========================================================================== */
(function () {
  'use strict';
  var REDUCE = matchMedia('(prefers-reduced-motion: reduce)');
  if (!window.PointerEvent) return;

  /* ---------------- motion kit ---------------- */
  var TIME = 1, BOUNCE = false;
  function damp() { return BOUNCE ? 0.8 : 1; }

  function Spring(v, onUpdate) {
    this.x = v; this.v = 0; this.target = v; this.damping = 1; this.response = 0.4;
    this.onUpdate = onUpdate; this.onRest = null; this.running = false; this.last = 0;
  }
  Spring.prototype.jump = function (x) { this.x = x; this.v = 0; this.onUpdate(this.x); };
  Spring.prototype.stop = function () { this.running = false; return this.v; };
  Spring.prototype.set = function (t, o) {
    o = o || {};
    this.target = t;
    if (o.velocity !== undefined) this.v = o.velocity;
    if (o.damping !== undefined) this.damping = o.damping;
    if (o.response !== undefined) this.response = o.response;
    this.onRest = o.onRest || null; this.start();
  };
  Spring.prototype.start = function () {
    if (this.running) return;
    if (REDUCE.matches) { this.jump(this.target); if (this.onRest) this.onRest(); return; }
    this.running = true; this.last = performance.now();
    var self = this;
    function tick(now) {
      if (!self.running) return;
      var dt = Math.min((now - self.last) / 1000, 0.064) * TIME; self.last = now;
      var w = 2 * Math.PI / self.response, z = self.damping, S = 1 / 240;
      while (dt > 0) {
        var h = Math.min(dt, S); dt -= h;
        var a = -w * w * (self.x - self.target) - 2 * z * w * self.v;
        self.v += a * h; self.x += self.v * h;
      }
      self.onUpdate(self.x);
      if (Math.abs(self.x - self.target) < 0.05 && Math.abs(self.v) < 0.05) {
        self.x = self.target; self.v = 0; self.onUpdate(self.x); self.running = false;
        if (self.onRest) self.onRest(); return;
      }
      requestAnimationFrame(tick);
    }
    requestAnimationFrame(tick);
  };
  function project(v, d) { d = d || 0.998; return (v / 1000) * d / (1 - d); }
  function rubber(over, dim, c) { c = c || 0.55; return (over * dim * c) / (dim + c * Math.abs(over)); }
  function Tracker() { this.h = []; }
  Tracker.prototype.clear = function () { this.h = []; };
  Tracker.prototype.add = function (p) {
    this.h.push({ p: p, t: performance.now() });
    if (this.h.length > 6) this.h.shift();
  };
  Tracker.prototype.velocity = function () {
    if (this.h.length < 2) return 0;
    var b = this.h[this.h.length - 1];
    if (performance.now() - b.t > 90) return 0;   /* held still: no momentum */
    var a = this.h[0], dt = (b.t - a.t) / 1000;
    return dt > 0 ? (b.p - a.p) / dt : 0;
  };
  function buzz(ms) { if (navigator.vibrate && !REDUCE.matches) { try { navigator.vibrate(ms || 8); } catch (e) {} } }
  function lerp(a, b, t) { return a + (b - a) * t; }
  /* the visual-viewport riders must move chrome for the KEYBOARD only.
     iOS also resizes the visual viewport for its own browser bars, and
     riding that parked the tab bar mid-screen (CEO video, 22 Aug). */
  function kbFocused() {
    var a = document.activeElement;
    if (!a) return false;
    var t = a.tagName;
    return t === 'INPUT' || t === 'TEXTAREA' || t === 'SELECT' || a.isContentEditable === true;
  }

  /* Tabby's REAL promo widget in the quick sheet - the same integration
     the live site runs (CEO, 22 Aug: "We need the same exact snippet that
     is connected to the live website"). Same script URL, same parameters
     as snippets/tabby-installments.liquid; one script tag ever. The price
     is a MAJOR-unit number passed as an argument, never scraped - round
     30's lesson: the widget only ever failed here because the HTML-span
     money format made the card price parse to 0 and the guard skipped the
     mount. The round-24 static card is retired at the CEO's direction. */
  function tabbyMount(sel, kd) {
    if (!kd || kd <= 0) return;
    function mount() {
      try {
        new TabbyPromo({
          selector: sel, currency: 'KWD', price: kd, lang: 'en',
          installmentsCount: 4, publicKey: 'yourApiKey', merchantCode: 'string'
        });
      } catch (e) {}
    }
    if (window.TabbyPromo) { mount(); return; }
    var existing = document.querySelector('script[src="https://checkout.tabby.ai/tabby-promo.js"]');
    if (existing) { existing.addEventListener('load', mount); return; }
    var script = document.createElement('script');
    script.src = 'https://checkout.tabby.ai/tabby-promo.js';
    script.async = true;
    script.onload = mount;
    document.body.appendChild(script);
  }

  /* the OTF mark for JS-built chrome - the circled lotus, the only mark
     (CEO, 20 Aug), same geometry as the footer fmark, never redrawn */
  var MARKLET = '<svg class="marklet" viewBox="0 0 100 100" fill="none" stroke="currentColor" aria-hidden="true">' +
    '<circle cx="50" cy="50" r="45.5" stroke-width="5"/>' +
    '<g transform="translate(50 52) scale(0.72) translate(-50 -61)" stroke-width="6.5" stroke-linejoin="round" stroke-linecap="round">' +
    '<path d="M50,78 C42.5,70 42,60 50,52 C58,60 57.5,70 50,78 Z" transform="rotate(-68 50 78)"/>' +
    '<path d="M50,78 C42.5,70 42,60 50,52 C58,60 57.5,70 50,78 Z" transform="rotate(68 50 78)"/>' +
    '<path d="M50,78 C41.5,68 41,52 50,42 C59,52 58.5,68 50,78 Z" transform="rotate(-34 50 78)"/>' +
    '<path d="M50,78 C41.5,68 41,52 50,42 C59,52 58.5,68 50,78 Z" transform="rotate(34 50 78)"/>' +
    '<path d="M50,78 C40,66 39.5,45 50,32 C60.5,45 60,66 50,78 Z"/>' +
    '<path d="M50,70 C45.5,62 45,50 50,43 C55,50 54.5,62 50,70 Z" stroke-width="4.5"/>' +
    '</g></svg>';

  /* KWD money: Shopify js APIs give minor units; KWD carries 3 decimals */
  /* MONEY SCALE - round 44. Every Shopify surface this file reads gives
     money in HUNDREDTHS of a dinar: cart.js and products.js return 7700 for
     a 77.000 KD bouquet, and Liquid's variant.price does the same (data-vraw
     is 3100 for 31.000 KD). KD() used to divide by 1000, so the whole bag
     read one tenth of the truth - a 152 KD cart showed as 15.200 KD, and
     because the 45 KD threshold was written in the same wrong scale, free
     delivery only unlocked at 450 KD. The rest of this file already divides
     by 100 in five places; this is now the only scale in use. */
  /* 5 Sep 2026 (CEO: "fix all inconsistencies"): the unit follows the page
     language - western digits, then د.ك on Arabic, KD on English - the same
     rule every Fluid string and the fluid-money snippet follow. */
  var KD_AR = (document.documentElement.lang || '').indexOf('ar') === 0;
  var KD_UNIT = KD_AR ? ' د.ك' : ' KD';
  function KD(minor) { return (minor / 100).toFixed(3) + (KD_AR ? ' د.ك' : ' KD'); }
  var FREE_AT_MINOR = 4500, DELIVERY_MINOR = 300;

  /* strings the JS builds, in the customer's language. fluid-bar renders the
     Arabic (or English) onto data-t-* attributes of .fluid-bag; this reads
     them lazily - the bag exists in the section markup before any render()
     runs - and falls back to the English so a stale bar can never blank
     the UI. Same pattern as the countdown's data-t-* bridge. */
  var I18N_EL = null;
  function TT(name, fb) {
    if (!I18N_EL) { I18N_EL = document.querySelector('.fluid-bag'); }
    var v = I18N_EL ? I18N_EL.getAttribute('data-t-' + name) : '';
    return ((v || '').trim()) || fb;
  }

  /* product-derived text going into innerHTML. The attributes were escaped
     by Liquid, but getAttribute hands back the DECODED string - a title
     like "Roses & Lilies" re-entered markup raw. Found in the 27 Aug sweep;
     the search layer already did this right with its own esc(). */
  function escT(t) {
    return String(t == null ? '' : t)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;')
      .replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  /* one bag line and one set of totals, written once and used by both the
     bag sheet and the cart page — two copies of this drifted apart once */
  function lineHTML(it) {
    return '<div class="fluid-bline-wrap">' +
      '<div class="fluid-bacts"><button class="bdel" data-del="' + it.key + '">' + TT('delete', 'Delete') + '</button></div>' +
      '<div class="fluid-bline">' +
      '<img src="' + (it.image || '') + '" alt="">' +
      '<div class="fluid-blt"><div class="fluid-bln">' + it.product_title + '</div>' +
      '<div class="fluid-blq">' +
        '<button data-k="' + it.key + '" data-d="-1" aria-label="' + TT('less', 'Less') + '">&minus;</button>' +
        '<span>' + it.quantity + '</span>' +
        '<button data-k="' + it.key + '" data-d="1" aria-label="' + TT('more', 'More') + '">+</button>' +
      '</div></div>' +
      '<div class="fluid-blp">' + KD(it.final_line_price) + '</div></div></div>';
  }
  function totalsHTML(flowers) {
    var free = flowers >= FREE_AT_MINOR;
    return '<div class="fluid-trow"><span>' + TT('flowers', 'Flowers') + '</span><b>' + KD(flowers) + '</b></div>' +
      '<div class="fluid-trow"><span>' + TT('delivery', 'Delivery') + '</span><b>' + (free ? TT('free', 'Free') : KD(DELIVERY_MINOR)) + '</b></div>' +
      '<div class="fluid-progress" role="img" aria-label="' + TT('progress', 'Free delivery progress') + '"><i></i></div>' +
      '<div class="fluid-pcap">' + (free
        ? TT('freecap', 'Free delivery unlocked - over 45 KD of flowers.')
        : TT('away', '_AMT_ away from free delivery.').replace('_AMT_', KD(FREE_AT_MINOR - flowers))) + '</div>' +
      '<div class="fluid-trow big"><span>' + TT('total', 'Total') + '</span><b>' + KD(flowers + (free ? 0 : DELIVERY_MINOR)) + '</b></div>';
  }

  /* ------------- the cash gift in the cart (28 Aug: standalone) -----------
     The gift is its own product now; the customer adds any bouquet next.
     Two rules, both enforced here because every road to checkout runs
     through this file:
     1. A cash gift can never check out alone - there must be a bouquet.
     2. The fee line is settled against the FINAL cart at the checkout gate:
        20% of the gift, plus the 3 KD delivery folded in when the cash
        pushes the cart over Shopify's free-shipping bar while the flowers
        alone are under it. The fee product is priced 0.010 KD, so its
        QUANTITY is the fee in hundredths. */
  var GIFT_VID = 52792510644535, FEE_VID = 52792510939447;
  var CG_FEE_PCT = 20;
  function giftSplit(cart) {
    var out = { gift: null, fee: null, flowers: 0, other: false };
    if (!cart || !cart.items) return out;
    for (var i = 0; i < cart.items.length; i++) {
      var it = cart.items[i];
      if (it.variant_id === GIFT_VID) out.gift = it;
      else if (it.variant_id === FEE_VID) out.fee = it;
      else { out.other = true; out.flowers += it.final_line_price; }
    }
    return out;
  }
  function giftNeedsBouquet(cart) {
    var sp = giftSplit(cart);
    return !!sp.gift && !sp.other;
  }
  function giftFeeSettle(cart) {
    /* resolves to a cart whose fee line is correct; fixes it when it is not */
    var sp = giftSplit(cart);
    var H = { 'Content-Type': 'application/json' };
    if (!sp.gift) {
      if (!sp.fee) return Promise.resolve(cart);
      /* a fee with no gift is an orphan - remove it */
      return fetch('/cart/change.js', { method: 'POST', headers: H,
        body: JSON.stringify({ id: sp.fee.key, quantity: 0 })
      }).then(function (r) { return r.json(); });
    }
    var base = sp.gift.quantity * CG_FEE_PCT;   /* qty is KD, so this is minor */
    var fold = sp.flowers < FREE_AT_MINOR &&
      (sp.gift.quantity * 100 + sp.flowers + base) >= FREE_AT_MINOR;
    var want = base + (fold ? DELIVERY_MINOR : 0);
    if (sp.fee && sp.fee.quantity === want) return Promise.resolve(cart);
    if (sp.fee) {
      return fetch('/cart/change.js', { method: 'POST', headers: H,
        body: JSON.stringify({ id: sp.fee.key, quantity: want })
      }).then(function (r) { return r.json(); });
    }
    return fetch('/cart/add.js', { method: 'POST', headers: H,
      body: JSON.stringify({ items: [{ id: FEE_VID, quantity: want }] })
    }).then(function () { return fetch('/cart.js'); }).then(function (r) { return r.json(); });
  }
  function needBouquetHTML() {
    return '<div class="fluid-needbq"><p>' +
      TT('needbq', 'A cash gift travels inside a bouquet. Add one to check out.') + '</p>' +
      '<a class="fluid-press" href="/collections/our-bouquets">' +
      TT('pickbq', 'Choose a bouquet') + '</a></div>';
  }
  function giftTotalsHTML(cart) {
    /* one honest Total row for a cart that carries cash: the flowers rows
       and the free-delivery progress are about flowers, and with cash in
       the cart they mislead. Shopify free-ships at the cart total; when
       that is only true because of the cash, the fee line already carries
       the 3 KD (giftFeeSettle), so the label stays plain. */
    var sp = giftSplit(cart);
    var cartTotal = cart.total_price;
    var shipCharged = cartTotal < FREE_AT_MINOR;
    var lbl = shipCharged ? TT('ordtotaldel', 'Total including 3 KD delivery')
      : (sp.flowers >= FREE_AT_MINOR ? TT('ordtotalfree', 'Total, delivery free')
        : TT('total', 'Total'));
    return '<div class="fluid-trow big"><span>' + lbl + '</span><b>' +
      KD(cartTotal + (shipCharged ? DELIVERY_MINOR : 0)) + '</b></div>';
  }

  /* ---------------- boot ---------------- */
  /* the mark in the smallest place of all: the browser tab. Dawn only
     writes a favicon when the store has one set; when it does not, ours. */
  if (window.FLUID_MARK && !document.querySelector('link[rel~="icon"]')) {
    var fav = document.createElement('link');
    fav.rel = 'icon'; fav.type = 'image/svg+xml'; fav.href = window.FLUID_MARK;
    document.head.appendChild(fav);
  }

  var home = document.querySelector('.fluid-home');
  var bar = document.querySelector('.fluid-bar');

  /* press feedback on pointer-down; lifts the moment the finger travels,
     so cards never bob while a rail is being swiped */
  var pressPt = null;
  document.addEventListener('pointerdown', function (e) {
    if (REDUCE.matches) return;
    pressPt = { x: e.clientX, y: e.clientY };
    var el = e.target.closest('.fluid-press'); if (el) el.classList.add('down');
    var g = e.target.closest('.fluid-gcard');
    if (g) { g.classList.add('cardpress'); var gw = g.closest('.fluid-gwrap'); if (gw) gw.classList.add('haspress'); }
    var r = e.target.closest('.fluid-rcard:not(.drop)'); if (r) r.classList.add('cardpress');
  }, { passive: true });
  function liftAll() {
    var open = document.querySelectorAll('.fluid-press.down, .cardpress, .haspress');
    for (var i = 0; i < open.length; i++) { open[i].classList.remove('down'); open[i].classList.remove('cardpress'); open[i].classList.remove('haspress'); }
  }
  document.addEventListener('pointerup', function () { pressPt = null; liftAll(); }, { passive: true });
  document.addEventListener('pointercancel', function () { pressPt = null; liftAll(); }, { passive: true });
  document.addEventListener('pointermove', function (e) {
    if (pressPt && (Math.abs(e.clientX - pressPt.x) > 8 || Math.abs(e.clientY - pressPt.y) > 8)) {
      pressPt = null; liftAll(); return;
    }
    if (document.querySelector('.down, .cardpress') && !e.target.closest('.fluid-press, .fluid-gcard, .fluid-rcard')) liftAll();
  }, { passive: true });

  if (bar) addEventListener('scroll', function () { bar.classList.toggle('stuck', scrollY > 10); }, { passive: true });

  /* bag count, shared */
  var bagcount = document.querySelector('.fluid-bagcount');
  var countSp = bagcount ? new Spring(1, function (s) { bagcount.style.transform = 'scale(' + s + ')'; }) : null;
  function setCount(n) {
    if (!bagcount) return;
    bagcount.textContent = n > 0 ? n : '';
    if (countSp) { countSp.jump(1); countSp.set(1, { velocity: 8, damping: 0.55, response: 0.3 }); }
  }

  /* ---------------- home-only features ---------------- */
  if (home) {

  /* ---------------- load choreography (once) ---------------- */
  (function () {
    var els = home.querySelectorAll('.fluid-reveal');
    if (REDUCE.matches) { for (var i = 0; i < els.length; i++) els[i].classList.add('in'); return; }
    for (var j = 0; j < els.length; j++) (function (el, k) {
      setTimeout(function () { el.classList.add('in'); }, 60 + k * 55);
    })(els[j], j);
  })();

  /* ---------------- countdown to the 9pm cutoff: the clock line ----------
     CEO, 23 Aug: the striped bar read as a file downloading, so the bar is
     gone. The time left is the whole object now.
     Every string is read off the element (data-t-*), which is filled by
     fluid-t in Liquid - so the soon and closed states are Arabic on the
     Arabic site. Nothing customer-facing is written in this file. */
  (function () {
    var box = home.querySelector('[data-cut]'); if (!box) return;
    var lbl = box.querySelector('[data-cutlbl]'), val = box.querySelector('[data-cutval]');
    var note = box.querySelector('[data-cutnote]');
    var seg = box.querySelector('[data-cutseg]');
    function T(name) { return (box.getAttribute('data-t-' + name) || '').trim(); }
    function part(txt, cls) {
      var sp = document.createElement('span');
      if (cls) { sp.className = cls; }
      sp.textContent = txt; return sp;
    }
    /* the twelve hours of the order day. Block 12 is the leftmost - the 9am
       hour - so a block is still available when the hours remaining reach it.
       The hour being lived is the one just past the last whole hour left. */
    function paintSeg(mins) {
      if (!seg) return;
      var kids = seg.children, n = kids.length;
      var full = Math.floor(mins / 60), partial = (mins % 60) > 0;
      for (var i = 0; i < n; i++) {
        var rem = n - i, cls = '';
        if (mins > 0) {
          if (rem <= full) { cls = 'on'; }
          else if (partial && rem === full + 1) { cls = 'now'; }
        }
        kids[i].className = cls;
      }
    }
    function tick() {
      var now = new Date();
      var kw = new Date(now.toLocaleString('en-US', { timeZone: 'Asia/Kuwait' }));
      var mins = (21 - kw.getHours()) * 60 - kw.getMinutes();
      if (mins > 0) {
        var h = Math.floor(mins / 60), m = mins % 60;
        var soon = mins <= 120;
        if (lbl) lbl.textContent = soon ? T('soonhead') : T('head');
        /* hours and minutes are separate isolated spans on purpose. As ONE
           string, "7s 34d" in Arabic came out of the bidi algorithm as
           "7d34 s" - the unit letters swapped, so it read 7 minutes 34 hours.
           Isolated, each number keeps its own unit whatever the direction,
           and the word rides in its own span after them - which in Arabic
           lands it on the left, where a right-to-left reader ends. */
        if (val) {
          while (val.firstChild) { val.removeChild(val.firstChild); }
          if (h > 0) { val.appendChild(part(h + T('h'))); }
          /* on the hour the minutes are zero, and "12h 0m left" reads badly */
          if (m > 0 || h === 0) { val.appendChild(part(m + T('m'))); }
          if (T('left')) { val.appendChild(part(T('left'), 'cutsuf')); }
        }
        paintSeg(mins);
        box.classList.remove('over');
        box.classList.toggle('soon', soon);
        if (note) note.textContent = soon ? T('soonnote') : T('note');
      } else {
        if (lbl) lbl.textContent = T('overhead');
        if (val) val.textContent = T('overval');
        paintSeg(0);
        box.classList.remove('soon');
        box.classList.add('over');
        if (note) note.textContent = T('overnote');
      }
    }
    tick(); setInterval(tick, 30000);
  })();

  /* ---------------- hero: lights the ambient, and This week scrolls ------ */
  (function () {
    var heroEl = home.querySelector('.fluid-hero');
    if (heroEl) requestAnimationFrame(function () { Ambient.show(heroEl.getAttribute('data-amb')); });
    var sg = home.querySelector('[data-scrollgrid]');
    var grid = home.querySelector('.fluid-grid');
    if (sg && grid) sg.addEventListener('click', function () {
      buzz(6);
      if (window.fluidReroll) window.fluidReroll();
      grid.scrollIntoView({ behavior: REDUCE.matches ? 'auto' : 'smooth', block: 'start' });
    });
  })();

  /* ---------------- the deal: eight of twenty-four -----------------------
     The home grid carries 24 server-rendered cards and each visit deals a
     different eight (CEO, 21 Aug: "so they see different options they might
     miss"). The deal is seeded per session, so going back does not reshuffle
     what they were just looking at; This week deals a fresh eight on tap. */
  (function () {
    var grid = home.querySelector('.fluid-grid');
    if (!grid) return;
    var wraps = grid.querySelectorAll('.fluid-gwrap');
    if (wraps.length <= 8) return;
    var seed = 0;
    try { seed = parseInt(sessionStorage.getItem('flmix') || '0', 10); } catch (err) {}
    if (!seed) {
      seed = (Date.now() % 899981) + 23;
      try { sessionStorage.setItem('flmix', String(seed)); } catch (err) {}
    }
    var s = seed;
    function rnd() { s = (s * 48271) % 2147483647; return s / 2147483647; }
    function deal() {
      var idx = [], i, j, t;
      for (i = 0; i < wraps.length; i++) idx.push(i);
      for (i = idx.length - 1; i > 0; i--) {
        j = Math.floor(rnd() * (i + 1));
        t = idx[i]; idx[i] = idx[j]; idx[j] = t;
      }
      grid.classList.add('gmix');
      for (i = 0; i < wraps.length; i++) wraps[i].classList.remove('gon');
      for (i = 0; i < 8 && i < idx.length; i++) wraps[idx[i]].classList.add('gon');
    }
    deal();
    window.fluidReroll = deal;
    /* the redeal button (CEO, 21 Aug): refresh live, right in front of them */
    document.addEventListener('click', function (e) {
      var b = e.target.closest('[data-redeal]');
      if (!b) return;
      buzz(7);
      grid.classList.add('swapping');
      setTimeout(function () { deal(); grid.classList.remove('swapping'); }, 180);
    });
  })();

  /* ---------------- ambient light: the page is lit by the active card ---- */
  var Ambient = (function () {
    var wrap = home.querySelector('.fluid-ambient');
    if (!wrap || REDUCE.matches) return { show: function () {} };
    var imgs = wrap.querySelectorAll('img');
    if (imgs.length < 2) return { show: function () {} };
    var front = 0, current = '';
    var springs = [
      new Spring(0, function (o) { imgs[0].style.opacity = o; }),
      new Spring(0, function (o) { imgs[1].style.opacity = o; })
    ];
    return {
      show: function (src) {
        if (!src || src === current) return;
        current = src;
        var back = 1 - front;
        imgs[back].src = src;
        springs[back].set(0.38, { damping: 1, response: 0.9 });
        springs[front].set(0, { damping: 1, response: 0.9 });
        front = back;
      }
    };
  })();

  /* ---------------- chips: page links now; the capsule module stays for
     any chips row that still carries a capsule div ---------------- */
  (function () {
    var chipsEl = home.querySelector('.fluid-chips'), capsule = home.querySelector('.fluid-capsule');
    if (!chipsEl || !capsule) return;
    var capX = new Spring(0, function (x) { capsule.style.transform = 'translate3d(' + x + 'px,0,0)'; });
    var capW = new Spring(0, function (w) { capsule.style.width = w + 'px'; });
    function place(chip, instant) {
      var x = chip.offsetLeft, w = chip.offsetWidth;
      if (instant) { capX.jump(x); capW.jump(w); return; }
      capX.set(x, { damping: damp(), response: 0.35 });
      capW.set(w, { damping: 1, response: 0.35 });
    }
    chipsEl.addEventListener('click', function (e) {
      var chip = e.target.closest('.fluid-chip'); if (!chip) return;
      var all = chipsEl.querySelectorAll('.fluid-chip');
      for (var i = 0; i < all.length; i++) all[i].setAttribute('aria-pressed', 'false');
      chip.setAttribute('aria-pressed', 'true');
      place(chip); buzz(6);
    });
    requestAnimationFrame(function () { var c = chipsEl.querySelector('.fluid-chip'); if (c) place(c, true); });
    addEventListener('resize', function () {
      var c = chipsEl.querySelector('[aria-pressed="true"]'); if (c) place(c, true);
    });
  })();

  /* ---------------- rail: physics + tilt + parallax + ambient ------------ */
  (function () {
    var vp = home.querySelector('.fluid-rail'), track = home.querySelector('.fluid-track');
    if (!vp || !track) return;
    if (REDUCE.matches) { vp.style.overflowX = 'auto'; return; }
    var cards = track.children;

    /* velocity tilt + in-card parallax ride on the same spring update */
    var lastX = 0, lastT = performance.now();
    var tilt = new Spring(0, function (deg) {
      for (var i = 0; i < cards.length; i++) {
        var face = cards[i].querySelector('.face');
        if (face) face.style.transform = 'rotate(' + deg + 'deg)' + (cards[i].classList.contains('cardpress') ? ' scale(.97)' : '');
      }
    });
    function parallax() {
      var vw = vp.clientWidth;
      for (var i = 0; i < cards.length; i++) {
        var img = cards[i].querySelector('img.shot'); if (!img) continue;
        var r = cards[i].getBoundingClientRect();
        var mid = r.left + r.width / 2 - vw / 2;
        img.style.transform = 'translate3d(' + (mid * -0.06) + 'px,0,0)';
      }
    }
    var sp = new Spring(0, function (x) {
      track.style.transform = 'translate3d(' + x + 'px,0,0)';
      var now = performance.now(), dt = now - lastT;
      if (dt > 0) {
        var vel = (x - lastX) / dt * 1000;
        var target = Math.max(-3.5, Math.min(3.5, vel / 900));
        tilt.set(target, { damping: 1, response: 0.5 });
      }
      lastX = x; lastT = now;
      parallax();
    });

    var step = 254, span = 0, index = 0, drag = null, suppress = false;
    var tr = new Tracker();
    function measure() {
      span = Math.max(0, track.scrollWidth - vp.clientWidth);
      if (cards.length > 1) step = cards[1].getBoundingClientRect().left - cards[0].getBoundingClientRect().left || 254;
    }
    measure(); addEventListener('resize', measure);
    function maxI() { return Math.ceil(span / step); }
    function announce(i) {
      var c = cards[Math.min(i, cards.length - 1)];
      if (c) Ambient.show(c.getAttribute('data-amb'));
    }
    announce(0);

    vp.addEventListener('dragstart', function (e) { e.preventDefault(); });
    vp.addEventListener('click', function (e) { if (suppress) { e.preventDefault(); e.stopPropagation(); } }, true);
    vp.addEventListener('pointerdown', function (e) {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      measure(); vp.setPointerCapture(e.pointerId); sp.stop();
      drag = { x: e.clientX, y: e.clientY, base: sp.x, axis: null }; suppress = false;
      tr.clear(); tr.add(e.clientX);
    });
    vp.addEventListener('pointermove', function (e) {
      if (!drag) return;
      var dx = e.clientX - drag.x, dy = e.clientY - drag.y;
      if (!drag.axis) {
        if (Math.abs(dx) < 10 && Math.abs(dy) < 10) return;
        drag.axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
        if (drag.axis === 'y') { drag = null; return; }
      }
      e.preventDefault(); tr.add(e.clientX);
      var x = drag.base + dx;
      if (x > 0) x = rubber(x, vp.clientWidth);
      if (x < -span) x = -span + rubber(x + span, vp.clientWidth);
      sp.jump(x);
    });
    function up() {
      if (!drag) return;
      var moved = drag.axis === 'x'; drag = null;
      if (!moved) return;
      suppress = true; setTimeout(function () { suppress = false; }, 0);
      var v = tr.velocity();
      var i = Math.round(-(sp.x + project(v)) / step);
      i = Math.max(0, Math.min(maxI(), i));
      if (i !== index) buzz(8);
      index = i; announce(i);
      sp.set(-Math.min(i * step, span), { velocity: v, damping: damp(), response: 0.4 });
    }
    vp.addEventListener('pointerup', up);
    vp.addEventListener('pointercancel', up);

    var pill = home.querySelector('.fluid-droppill');
    var gridsec = home.querySelector('.fluid-sec');
    if (pill && gridsec) pill.addEventListener('click', function () {
      gridsec.scrollIntoView({ behavior: REDUCE.matches ? 'auto' : 'smooth' });
    });
  })();

  }

  /* ---------------- scroll lock ---------------- */
  var lockY = 0, locks = 0;
  function lockScroll() {
    if (++locks > 1) return; lockY = scrollY;
    document.body.style.position = 'fixed';
    document.body.style.top = (-lockY) + 'px';
    document.body.style.left = '0'; document.body.style.right = '0';
    document.body.style.overflow = 'hidden';
  }
  function unlockScroll() {
    if (--locks > 0) return;
    document.body.style.position = ''; document.body.style.top = '';
    document.body.style.left = ''; document.body.style.right = '';
    document.body.style.overflow = '';
    scrollTo(0, lockY);
  }

  /* ---------------- product sheet: the morph ---------------- */
  var scrim = document.querySelector('.fluid-scrim');
  var openSheet = null;
  /* 27 Aug sweep: close a sheet, instantly open another, and the OLD close
     spring kept writing the scrim down each frame, then its onRest switched
     the scrim off under the new sheet. Each open or close takes a fresh
     generation; a spring from a stale generation may finish its own element
     but must leave the shared scrim alone. */
  var sheetGen = 0;
  var galClose = null;
  var srchClose = null;
  var ordClose = null;
  var custClose = null;
  var pgClose = null;

  function sheetFromCard(card) {
    if (openSheet || !scrim) return;
    sheetGen++;
    var wrap = document.querySelector('.fluid-home-wrap, .fluid-page-wrap');
    if (!wrap) return;
    var d = card.dataset;
    var r0 = card.getBoundingClientRect();
    var shell = wrap.getBoundingClientRect();
    var r1 = { left: shell.left, top: 0, width: shell.width, height: innerHeight };
    var el = document.createElement('div');
    el.className = 'fluid-sheet';
    el.innerHTML =
      '<div class="panelglow"><img src="' + (d.amb || d.img) + '" alt=""></div>' +
      /* round 41 (CEO): the WHOLE quick view scrolls as one page - photo,
         caption and body live in one scroller, so the photo scrolls away
         like any product page. The close button sits OUTSIDE it and never
         scrolls off, because it is now the only way out. */
      '<button class="fluid-close fluid-press" aria-label="Close">&times;</button>' +
      '<div class="dscroll">' +
      '<div class="dhead">' +
      '<img class="dphoto" src="' + d.img + '" alt="">' +
      '<div class="pveil"></div>' +
      /* round 38 (CEO, third time of asking): the name and price ride the
         PHOTO over the same veil the shuffle card uses - they used to sit
         below it on cream in dark text, which is what he kept seeing,
         because the quick sheet is what a tapped card opens. The
         standalone product page was the only thing earlier passes fixed. */
      '<div class="dover">' +
        '<div class="fluid-dname">' + escT(d.title) + '</div>' +
        '<div class="fluid-dprice">' + escT(String(d.price || '').replace(/<[^>]*>/g, ' ')) + '</div>' +
      '</div>' +
      '<button class="fluid-galbtn fluid-press">' + TT('gallery', 'Gallery') + '</button>' +
      '</div>' +
      '<div class="dbody" style="width:' + r1.width + 'px">' +
        '<div class="fluid-dnote">' + escT(d.note || '') + '</div>' +
        '<div class="fluid-dvars" data-dvars></div>' +
        '<div class="fluid-drow">' +
          '<div class="fluid-stepper"><button data-s="-1" aria-label="' + TT('less', 'Less') + '">&minus;</button><span class="qv">1</span><button data-s="1" aria-label="' + TT('more', 'More') + '">+</button></div>' +
          '<button class="fluid-add fluid-press"' + (d.available === 'false' ? ' disabled' : '') + '>' + (d.available === 'false' ? TT('sold', 'Sold out') : TT('add', 'Add to Bag')) + '</button>' +
        '</div>' +
        '<div class="fluid-dfull" data-dfull></div>' +
        '<div data-shtops></div>' +
        '<div class="fluid-shpay">' +
          '<div id="TabbyPromoSheet"></div>' +
          '<div data-shdeema></div>' +
          '<div data-shbadges></div>' +
        '</div>' +
      '</div>' +
      '</div>';
    el.setAttribute('data-handle', d.handle || '');
    document.body.appendChild(el);
    morphChrome(0);   /* the Spring does not paint frame zero */
    card.style.visibility = 'hidden';
    lockScroll();
    scrim.style.pointerEvents = 'auto';

    /* the full product arrives quietly: gallery, the whole description in
       place of the card's clipped line, and the size choices when there is
       more than one (round 26 - the sheet IS the product page now) */
    fetch('/products/' + d.handle + '.js').then(function (r) { return r.json(); }).then(function (prod) {
      if (!el.isConnected) return;
      if (prod.variants && prod.variants[0] && !el.getAttribute('data-variant')) el.setAttribute('data-variant', prod.variants[0].id);
      if (prod.images && prod.images.length) el._gimgs = prod.images.map(function (u) {
        return u + (u.indexOf('?') > -1 ? '&' : '?') + 'width=1800';
      });
      if (prod.description) {
        var df = el.querySelector('[data-dfull]');
        var dn = el.querySelector('.fluid-dnote');
        if (df) { df.innerHTML = prod.description; if (dn) dn.style.display = 'none'; }
      }
      if (prod.variants && prod.variants.length > 1) {
        var dv = el.querySelector('[data-dvars]');
        if (dv) {
          var cur = String(el.getAttribute('data-variant') || '');
          var vh = '', vi, pv;
          for (vi = 0; vi < prod.variants.length; vi++) {
            pv = prod.variants[vi];
            vh += '<button class="fluid-var' + (String(pv.id) === cur ? ' on' : '') + '" type="button" data-vid="' + pv.id + '" data-vraw="' + pv.price + '"' + (pv.available ? '' : ' disabled') + '>' + escT(pv.title) + '</button>';
          }
          dv.innerHTML = vh;
        }
      }
      /* round 30: the raw variant price is the truth for money math - the
         card attribute is display text. Re-anchor the add total and the
         Tabby quarter on it, and if the card's price failed to parse and
         the Tabby line was skipped, clone it now. */
      var pv0 = null;
      if (prod.variants && prod.variants.length) {
        var cur3 = String(el.getAttribute('data-variant') || '');
        for (var pj = 0; pj < prod.variants.length; pj++) {
          if (String(prod.variants[pj].id) === cur3) { pv0 = prod.variants[pj]; break; }
        }
        if (!pv0) pv0 = prod.variants[0];
      }
      if (pv0 && pv0.price > 0) {
        el.setAttribute('data-vraw', String(pv0.price));
        if (Math.round(kd4 * 100) !== pv0.price) tabbyMount('#TabbyPromoSheet', pv0.price / 100);
      }
    }).catch(function () {});
    if (d.variant) el.setAttribute('data-variant', d.variant);

    /* the payment pair, same cards as the product page (CEO, 22 Aug).
       Tabby first, deema second - the order is a rule.
       Round 30: the store's money format wraps prices in an HTML span, so
       data-price arrives as markup and a bare parseFloat read NaN - which
       silently skipped this card on EVERY real product (the rig had
       hand-typed clean prices, so it never caught it). Tags are stripped
       before parsing, and the product fetch below re-fills the amount
       from the variant's raw price either way. */
    var kd4 = parseFloat(String(d.price).replace(/<[^>]*>/g, ' ').replace(/,/g, '')) || 0;
    el.setAttribute('data-vraw', String(Math.round(kd4 * 100)));
    tabbyMount('#TabbyPromoSheet', kd4);

    /* the topper rail, same cards as the product page (round 26), unless
       this product is itself a topper, an add-on, a gift card or a
       subscription - the card's data-topok carries the page's own test */
    var topsT = document.getElementById('fl-tops');
    var shtp = el.querySelector('[data-shtops]');
    if (topsT && topsT.content && shtp && d.topok !== 'false') shtp.appendChild(topsT.content.cloneNode(true));
    var deemaT = document.getElementById('fl-deema');
    var shd = el.querySelector('[data-shdeema]');
    if (deemaT && deemaT.content && shd) shd.appendChild(deemaT.content.cloneNode(true));
    var badgeT = document.getElementById('fl-essbadges');
    var shb = el.querySelector('[data-shbadges]');
    if (badgeT && badgeT.content && shb) shb.appendChild(badgeT.content.cloneNode(true));

    var body = el.querySelector('.dbody');
    var headEl = el.querySelector('.dhead');
    var scrollEl = el.querySelector('.dscroll');
    var doverEl = el.querySelector('.dover');
    var dnameEl = el.querySelector('.dover .fluid-dname');
    var dpriceEl = el.querySelector('.dover .fluid-dprice');
    var galBtnEl = el.querySelector('.fluid-galbtn');
    var closeBtnEl = el.querySelector('.fluid-close');
    /* round 37 (CEO video: closing "glitches") + round 38: at card size the
       sheet IS the card - photo fills the box, the veil and the caption
       cover it, the floating chrome is gone - and it grows continuously
       into the open sheet. No frame of the morph shows a half-empty card. */
    function morphChrome(q) {
      var m = Math.min(Math.max(q, 0), 1);
      /* eased, not linear: the card look resolves EARLY in the close, so
         the photo covers the shrinking box before it reaches card size.
         Linear left a pale sliver of sheet under the photo mid-morph -
         the last of the CEO's "glitches when closed". */
      var e = m * m;
      var ph = 100 - 55 * e;
      /* one height to animate: the photo, veil and caption all fill the
         head, so they follow it without four separate tweens. */
      if (headEl) headEl.style.height = ph + '%';
      if (doverEl) doverEl.style.padding = (13 + 3 * e) + 'px ' + (14 + 4 * e) + 'px';
      if (dnameEl) dnameEl.style.fontSize = (16 + 10 * e) + 'px';
      if (dpriceEl) dpriceEl.style.fontSize = (14 + 3 * e) + 'px';
      if (galBtnEl) galBtnEl.style.opacity = String(m);
      if (closeBtnEl) closeBtnEl.style.opacity = String(m);
    }
    function setRect(t) {
      var q = Math.max(0, t);
      el.style.left = lerp(r0.left, r1.left, q) + 'px';
      el.style.top = lerp(r0.top, r1.top, q) + 'px';
      el.style.width = lerp(r0.width, r1.width, q) + 'px';
      el.style.height = lerp(r0.height, r1.height, q) + 'px';
      el.style.borderRadius = lerp(20, 0, Math.min(q, 1)) + 'px';
      scrim.style.opacity = Math.min(q, 1);
      body.style.opacity = Math.max(0, (q - 0.55) / 0.45);
      body.style.transform = 'translate3d(0,' + ((1 - Math.min(q, 1)) * 30) + 'px,0)';
      morphChrome(Math.min(q, 1));
    }
    function setShrink(s) {
      var sc = 1 - 0.3 * Math.max(0, s);
      el.style.transform = 'translate3d(0,' + (120 * Math.max(0, s)) + 'px,0) scale(' + sc + ')';
      el.style.transformOrigin = '50% 0';
      el.style.borderRadius = (26 * Math.min(Math.max(s, 0), 1)) + 'px';
      el.classList.toggle('dragring', s > 0.02);
    }
    var rect = new Spring(0, setRect);
    var shrink = new Spring(0, setShrink);
    var stack = new Spring(0, function (k) {   /* pushed back while the bag is over it */
      el.style.transform = 'scale(' + (1 - 0.06 * k) + ')';
      el.style.transformOrigin = '50% 0';
      el.style.filter = 'brightness(' + (1 - 0.25 * k) + ')';
      el.style.borderRadius = (18 * k) + 'px';
    });
    openSheet = { el: el, card: card, rect: rect, shrink: shrink, stack: stack, qty: 1, morph: morphChrome };
    rect.set(1, { damping: 1, response: 0.42, onRest: function () { el.classList.add('opened'); } });

    var qty = 1;
    function updAddSheet() {
      var btn = el.querySelector('.fluid-add');
      if (!btn || btn.disabled) return;
      var raw = parseInt(el.getAttribute('data-vraw') || '0', 10) * qty;
      var tops = el.querySelectorAll('[data-topper]:checked'), ti;
      for (ti = 0; ti < tops.length; ti++) raw += parseInt(tops[ti].getAttribute('data-tprice') || '0', 10);
      if (tops.length && raw > 0) btn.textContent = TT('add', 'Add to Bag') + ' ' + String.fromCharCode(183) + ' ' + (raw / 100).toFixed(3) + KD_UNIT;
      else if (btn.textContent.indexOf(String.fromCharCode(183)) > -1) btn.textContent = TT('add', 'Add to Bag');
    }
    el.addEventListener('change', function (e) {
      if (e.target && e.target.hasAttribute && e.target.hasAttribute('data-topper')) { buzz(6); updAddSheet(); }
    });
    el.addEventListener('click', function (e) {
      if (e.target.closest('.fluid-close')) { closeSheet(0); return; }
      if (e.target.closest('.fluid-galbtn') || (e.target.classList && e.target.classList.contains('dphoto'))) {
        openGallery(el._gimgs && el._gimgs.length ? el._gimgs : [d.img]);
        return;
      }
      var s = e.target.closest('[data-s]');
      if (s) {
        qty = Math.max(1, Math.min(9, qty + parseInt(s.getAttribute('data-s'), 10)));
        el.querySelector('.qv').textContent = qty; openSheet.qty = qty; buzz(5); updAddSheet(); return;
      }
      var vb = e.target.closest('.fluid-var');
      if (vb && !vb.disabled) {
        var vs = el.querySelectorAll('.fluid-var'), vj;
        for (vj = 0; vj < vs.length; vj++) vs[vj].classList.remove('on');
        vb.classList.add('on');
        el.setAttribute('data-variant', vb.getAttribute('data-vid'));
        var vraw2 = parseInt(vb.getAttribute('data-vraw') || '0', 10);
        el.setAttribute('data-vraw', String(vraw2));
        var dp = el.querySelector('.fluid-dprice');
        if (dp) dp.textContent = (vraw2 / 100).toFixed(3) + KD_UNIT;
        tabbyMount('#TabbyPromoSheet', vraw2 / 100);
        updAddSheet();
        buzz(6);
        return;
      }
      if (e.target.closest('.fluid-add') && !e.target.closest('[disabled]')) addFromSheet(el, qty);
    });

    /* round 41 (CEO): "no exit except the x button on the top left". The
       swipe-to-dismiss drag is GONE - it fought the new full-page scroll
       (a grab on the photo to scroll used to throw the sheet shut) and it
       was the accidental exit. The sheet now closes only by the X, or by
       adding to bag, which is the flow completing rather than an escape. */
  }

  function closeSheet(vs) {
    if (!openSheet) return;
    var el = openSheet.el, card = openSheet.card, shrink = openSheet.shrink;
    buzz(9);
    shrink.stop(); openSheet.rect.stop();
    var cur = el.getBoundingClientRect();
    var fromR = parseFloat(el.style.borderRadius) || 0;
    el.style.transform = ''; el.style.filter = '';
    el.style.left = cur.left + 'px'; el.style.top = cur.top + 'px';
    el.style.width = cur.width + 'px'; el.style.height = cur.height + 'px';
    var r0 = card.getBoundingClientRect();
    var from = { left: cur.left, top: cur.top, width: cur.width, height: cur.height, r: fromR };
    var body = el.querySelector('.dbody');
    var chrome = openSheet.morph;
    /* the morph runs from the top of the page, and scrolling is off while
       it runs, so a scrolled sheet cannot shear mid-flight */
    el.classList.remove('opened');
    var sc0 = el.querySelector('.dscroll');
    if (sc0) sc0.scrollTop = 0;
    function setT(t) {
      var q = Math.min(Math.max(t, 0), 1);
      el.style.left = lerp(from.left, r0.left, q) + 'px';
      el.style.top = lerp(from.top, r0.top, q) + 'px';
      el.style.width = lerp(from.width, r0.width, q) + 'px';
      el.style.height = lerp(from.height, r0.height, q) + 'px';
      el.style.borderRadius = lerp(from.r, 20, q) + 'px';
      if (gen === sheetGen) scrim.style.opacity = 1 - q;
      body.style.opacity = Math.max(0, 1 - q * 2.4);
      if (chrome) chrome(1 - q);
    }
    var gen = ++sheetGen;
    var out = new Spring(0, setT);
    openSheet = null;
    out.set(1, { velocity: Math.max(0, vs || 0), damping: 1, response: 0.4, onRest: function () {
      card.style.visibility = '';
      el.remove();
      if (gen === sheetGen) { scrim.style.opacity = 0; scrim.style.pointerEvents = 'none'; }
      unlockScroll();
    } });
  }
  /* ---------------- fullscreen gallery (Apple Store style) --------------- */
  /* round 42 - the gallery. Two things the CEO asked for here:
     (1) "allow them to be able to zoom in to the photos using two fingers.
     I want them to see the details and get mesmerized" - pinch to zoom;
     and (2) every
     product's photo framed at the SAME size, with nothing cropped off the
     bouquet. The frame (.gframe) is the unit that scales, so the photo
     keeps its rounded card as it grows and the blurred fill grows with it. */
  function openGallery(imgs) {
    if (galClose || !imgs || !imgs.length) return;
    var el = document.createElement('div');
    el.className = 'fluid-gal';
    var html = '<div class="gtrack">';
    for (var i = 0; i < imgs.length; i++) {
      html += '<div class="gslide"><div class="gframe">' +
        '<img class="gblur" src="' + imgs[i] + '" alt="" aria-hidden="true">' +
        '<img class="gph" src="' + imgs[i] + '" alt="">' +
        '</div></div>';
    }
    html += '</div><button class="gclose fluid-press" aria-label="Close">&times;</button>' +
      '<div class="gbar"><i></i></div>';
    el.innerHTML = html;
    document.body.appendChild(el);
    lockScroll();
    var track = el.querySelector('.gtrack'), bar = el.querySelector('.gbar'), seg = el.querySelector('.gbar i');
    var frames = el.querySelectorAll('.gframe');
    var n = imgs.length, index = 0, vw = innerWidth;
    function segsync(x) {
      var span = (n - 1) * vw;
      var p = span ? Math.min(1, Math.max(0, -x / span)) : 0;
      var bw = bar.clientWidth, sw = Math.max(28, bw / n);
      seg.style.width = sw + 'px';
      seg.style.transform = 'translate3d(' + (p * (bw - sw)) + 'px,0,0)';
    }
    var gx = new Spring(0, function (x) {
      track.style.transform = 'translate3d(' + x + 'px,0,0)';
      segsync(x);
    });
    gx.jump(0);
    /* rises from the bottom as a sheet; the product sheet pushes back */
    var rise = new Spring(1, function (t) {
      var q = Math.min(Math.max(t, 0), 1);
      el.style.transform = 'translate3d(0,' + (Math.max(t, 0) * 102) + '%,0)';
      el.style.borderRadius = (26 * q) + 'px ' + (26 * q) + 'px 0 0';
      if (openSheet) openSheet.stack.jump(1 - q);
    });
    rise.jump(1);
    rise.set(0, { damping: 1, response: 0.45 });

    /* ---------- pinch to zoom ---------- */
    var MAXZ = 4;
    var pts = {}, pinch = null, zel = null;
    var zs = 1, zx = 0, zy = 0;
    function zapply() {
      if (zel) zel.style.transform = 'translate3d(' + zx + 'px,' + zy + 'px,0) scale(' + zs + ')';
    }
    var zsp = new Spring(1, function (v) { zs = v; zapply(); });
    var zxp = new Spring(0, function (v) { zx = v; zapply(); });
    var zyp = new Spring(0, function (v) { zy = v; zapply(); });
    function zclaim() { if (!zel) zel = frames[index] || null; }
    function zreset() {
      zsp.stop(); zxp.stop(); zyp.stop();
      zs = 1; zx = 0; zy = 0;
      if (zel) zel.style.transform = '';
      zel = null;
    }
    /* round 43 (CEO: "make sure the zoom doesn't stay and the photo goes
       back to its original size"). The zoom belongs to the PINCH: while the
       two fingers are down the photo enlarges and follows them, and the
       moment they lift it springs back to its original size, centred. It is
       never left parked at 2x for the next customer to puzzle over. */
    function zhome() {
      zsp.jump(zs); zxp.jump(zx); zyp.jump(zy);
      zsp.set(1, { damping: damp(), response: 0.34 });
      zxp.set(0, { damping: damp(), response: 0.34 });
      zyp.set(0, { damping: damp(), response: 0.34 });
    }
    function ptlist() {
      var a = [], k;
      for (k in pts) if (pts.hasOwnProperty(k)) a.push(pts[k]);
      return a;
    }
    function pinchStart() {
      var a = ptlist();
      if (a.length < 2) return;
      drag = null;                       /* two fingers is never a swipe */
      gx.stop(); rise.stop(); zsp.stop(); zxp.stop(); zyp.stop();
      zclaim();
      if (!zel) return;
      var dx = a[0].x - a[1].x, dy = a[0].y - a[1].y;
      var r = zel.getBoundingClientRect();
      pinch = {
        d0: Math.max(1, Math.sqrt(dx * dx + dy * dy)),
        s0: zs, x0: zx, y0: zy,
        mx: (a[0].x + a[1].x) / 2, my: (a[0].y + a[1].y) / 2,
        /* the frame scales about its own centre, so scaling never moves the
           centre - the rest centre is just the current rect centre less the
           translate we put there. */
        cx: r.left + r.width / 2 - zx,
        cy: r.top + r.height / 2 - zy
      };
    }
    function pinchMove() {
      var a = ptlist();
      if (!pinch || a.length < 2) return;
      var dx = a[0].x - a[1].x, dy = a[0].y - a[1].y;
      var d = Math.max(1, Math.sqrt(dx * dx + dy * dy));
      var mx = (a[0].x + a[1].x) / 2, my = (a[0].y + a[1].y) / 2;
      var s = pinch.s0 * (d / pinch.d0);
      if (s > MAXZ) s = MAXZ + rubber(s - MAXZ, 2);
      if (s < 1) s = 1 - rubber(1 - s, 1, 0.9);
      /* hold the point between the fingers still, under the fingers */
      var k = s / pinch.s0;
      zs = s;
      zx = mx - pinch.cx - (pinch.mx - pinch.cx - pinch.x0) * k;
      zy = my - pinch.cy - (pinch.my - pinch.cy - pinch.y0) * k;
      zapply();
    }
    function pinchEnd() {
      if (!pinch) return;
      pinch = null;
      zhome();
    }
    /* iOS Safari zooms the PAGE on a two-finger pinch and touch-action does
       not stop it - this theme's viewport meta has no maximum-scale. The
       gesture events are the only reliable brake, and preventing them also
       stops Safari cancelling our pointers halfway through the pinch.
       Bound on the document only while the gallery is up. */
    function nogest(ev) { ev.preventDefault(); }
    document.addEventListener('gesturestart', nogest, { passive: false });
    document.addEventListener('gesturechange', nogest, { passive: false });
    document.addEventListener('gestureend', nogest, { passive: false });
    el.addEventListener('touchmove', function (ev) {
      if (ev.touches && ev.touches.length > 1) ev.preventDefault();
    }, { passive: false });

    function close(v) {
      if (!galClose) return; galClose = null;
      buzz(8);
      removeEventListener('resize', onRs);
      document.removeEventListener('gesturestart', nogest);
      document.removeEventListener('gesturechange', nogest);
      document.removeEventListener('gestureend', nogest);
      rise.set(1, { velocity: v || 0, damping: 1, response: 0.4, onRest: function () {
        el.remove(); unlockScroll();
      } });
    }
    galClose = close;
    function onRs() { vw = innerWidth; zreset(); gx.jump(-index * vw); }
    addEventListener('resize', onRs);
    el.querySelector('.gclose').addEventListener('click', function () { close(0); });

    /* swipe: one photo per gesture, rubber at the ends; drag down to leave.
       Two fingers zoom instead, and moving them while pinched carries the
       photo around under them - that is how the customer looks into a
       bouquet, and it ends when the fingers lift. */
    var drag = null; var trx = new Tracker(); var trv = new Tracker();
    el.addEventListener('pointerdown', function (e) {
      if (e.target.closest('button')) return;
      el.setPointerCapture(e.pointerId);
      pts[e.pointerId] = { x: e.clientX, y: e.clientY };
      if (ptlist().length > 1) { pinchStart(); return; }
      /* a zoom on its way back to its original size must be allowed to land.
         One finger during that third of a second does nothing rather than
         dragging a half-sized photo across the screen. */
      if (zs > 1.01) return;
      gx.stop(); rise.stop();
      zclaim();
      drag = { x: e.clientX, y: e.clientY, base: gx.x, rbase: Math.max(rise.x, 0), axis: null };
      trx.clear(); trx.add(e.clientX); trv.clear(); trv.add(e.clientY);
    });
    el.addEventListener('pointermove', function (e) {
      if (pts[e.pointerId]) { pts[e.pointerId].x = e.clientX; pts[e.pointerId].y = e.clientY; }
      if (pinch) { e.preventDefault(); pinchMove(); return; }
      if (!drag) return;
      var dx = e.clientX - drag.x, dy = e.clientY - drag.y;
      if (!drag.axis) {
        if (Math.abs(dx) < 10 && Math.abs(dy) < 10) return;
        drag.axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
      }
      e.preventDefault();
      if (drag.axis === 'x') {
        trx.add(e.clientX);
        var span = (n - 1) * vw;
        var x = drag.base + dx;
        if (x > 0) x = rubber(x, vw);
        if (x < -span) x = -span + rubber(x + span, vw);
        gx.jump(x);
        return;
      }
      trv.add(e.clientY);
      var t = drag.rbase + (dy > 0 ? dy / innerHeight : -rubber(-dy, innerHeight) / innerHeight);
      rise.jump(Math.max(t, -0.04));
    });
    function end() {
      if (!drag) return;
      var axis = drag.axis; drag = null;
      if (axis === 'x') {
        var v = trx.velocity();
        var target = Math.abs(v) > 260 ? index + (v < 0 ? 1 : -1) : Math.round(-gx.x / vw);
        target = Math.max(index - 1, Math.min(index + 1, target));
        target = Math.max(0, Math.min(n - 1, target));
        if (target !== index) { buzz(7); zreset(); }
        index = target;
        gx.set(-index * vw, { velocity: v, damping: damp(), response: 0.42 });
        return;
      }
      if (axis === 'y') {
        var vy = trv.velocity();
        var leave = Math.abs(vy) > 350 ? vy > 0 : rise.x > 0.32;
        if (leave) close(vy / innerHeight);
        else rise.set(0, { velocity: vy / innerHeight, damping: damp(), response: 0.4 });
      }
    }
    function up(e) {
      if (pts[e.pointerId]) delete pts[e.pointerId];
      if (pinch) { pinchEnd(); return; }
      end();
    }
    el.addEventListener('pointerup', up);
    el.addEventListener('pointercancel', up);
    el.addEventListener('dragstart', function (e) { e.preventDefault(); });
  }

  /* round 41: tapping the scrim no longer closes the quick view - the X is
     the only exit (CEO). Escape still works: it is keyboard-only, so no
     phone customer can trigger it by accident, and trapping keyboard
     users in a dialog is not acceptable. */
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape') return;
    if (galClose) { galClose(0); return; }
    if (ordClose) { ordClose(); return; }
    if (custClose) { custClose(); return; }
    if (pgClose) { pgClose(); return; }
    if (openSheet) { closeSheet(0); return; }
    if (srchClose) srchClose();
  });
  if (home) home.addEventListener('click', function (e) {
    var c = e.target.closest('.fluid-gcard'); if (c) { sheetFromCard(c); return; }
    var r = e.target.closest('.fluid-rcard:not(.drop) .face'); if (r) sheetFromCard(r);
  });
  /* the browse surfaces get the same morph — a card is a card everywhere */
  var pageEl = document.querySelector('.fluid-page');
  if (pageEl && !pageEl.classList.contains('fluid-pdp')) pageEl.addEventListener('click', function (e) {
    var c = e.target.closest('.fluid-gcard');
    if (c) { e.preventDefault(); sheetFromCard(c); }
  });

  /* ---------------- the flight: item springs into the bag ---------------- */
  function fly(fromEl, imgSrc, then) {
    if (REDUCE.matches || !bagcount || !imgSrc) { if (then) then(); return; }
    var a = fromEl.getBoundingClientRect(), b = bagcount.getBoundingClientRect();
    var img = document.createElement('img');
    img.className = 'fluid-fly'; img.src = imgSrc;
    document.body.appendChild(img);
    var x0 = a.left + a.width / 2 - 27, y0 = a.top - 10;
    var x1 = b.left + b.width / 2 - 27, y1 = b.top + b.height / 2 - 27;
    var done = 0;
    function arrive() { if (++done < 2) return; img.remove(); if (then) then(); }
    var sy = new Spring(y0, function () {});
    var sx = new Spring(x0, function (x) {
      var p = Math.abs((x - x0) / ((x1 - x0) || 1));
      img.style.transform = 'translate3d(' + x + 'px,' + sy.x + 'px,0) scale(' + Math.max(0.35, 1 - p * 0.65) + ')';
    });
    img.style.transform = 'translate3d(' + x0 + 'px,' + y0 + 'px,0)';
    sx.set(x1, { damping: 1, response: 0.5, onRest: arrive });
    sy.set(y1, { damping: 0.85, velocity: -600, response: 0.5, onRest: arrive });
  }

  /* ---------------- cart: real Shopify state ---------------- */
  var CART = null;
  function refreshCart() {
    return fetch('/cart.js').then(function (r) { return r.json(); }).then(function (c) {
      CART = c; setCount(c.item_count); return c;
    });
  }
  function addFromSheet(el, qty) {
    var variant = el.getAttribute('data-variant');
    var btn = el.querySelector('.fluid-add');
    if (!variant) {
      /* the quiet product fetch failed (flaky network) and the card carried
         no variant - Add used to eat every tap in silence (27 Aug sweep).
         Refetch on the tap itself, then run the add it was asked for. */
      var h = el.getAttribute('data-handle') || '';
      if (!h || el._refetching) return;
      el._refetching = true;
      if (btn) btn.disabled = true;
      fetch('/products/' + h + '.js').then(function (r) { return r.json(); }).then(function (prod) {
        el._refetching = false;
        if (btn) btn.disabled = false;
        if (prod.variants && prod.variants[0]) {
          el.setAttribute('data-variant', String(prod.variants[0].id));
          addFromSheet(el, qty);
        }
      }).catch(function () {
        el._refetching = false;
        if (btn) btn.disabled = false;
      });
      return;
    }
    btn.disabled = true;
    /* ticked toppers ride along, exactly as on the product page (round 26) */
    var items = [{ id: parseInt(variant, 10), quantity: qty }];
    var tops = el.querySelectorAll('[data-topper]:checked'), ti;
    for (ti = 0; ti < tops.length; ti++) items.push({ id: parseInt(tops[ti].getAttribute('data-tid'), 10), quantity: 1 });
    fetch('/cart/add.js', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: items })
    }).then(function (r) {
      if (!r.ok) throw new Error('add failed');
      return refreshCart();
    }).then(function () {
      buzz(10);
      var photo = el.querySelector('.dphoto');
      fly(btn, photo ? photo.src : '', null);
      setTimeout(function () { closeSheet(0); }, 120);
    }).catch(function () { btn.disabled = false; });
  }

  /* ---------------- bag sheet ---------------- */
  var bag = document.querySelector('.fluid-bag'), bscrim = document.querySelector('.fluid-bscrim');
  if (bag && bscrim) (function () {
    var bagH = 420;
    var bagSp = new Spring(1, function (t) {
      bag.style.transform = 'translate3d(0,' + (t * bagH) + 'px,0)';
      bscrim.style.opacity = Math.max(0, 1 - t);
      bscrim.style.pointerEvents = t < 0.98 ? 'auto' : 'none';
      if (openSheet) openSheet.stack.jump(Math.max(0, 1 - t));   /* sheet pushes back */
    });
    function measure() { bagH = bag.offsetHeight || 420; }
    var progSp = new Spring(0, function (x) {
      var i = bag.querySelector('.fluid-progress i'); if (i) i.style.transform = 'scaleX(' + x + ')';
    });

    function render() {
      var lines = bag.querySelector('.fluid-blines'), tot = bag.querySelector('.fluid-btotals');
      if (!CART || !CART.items.length) {
        lines.innerHTML = '<div class="fluid-bempty">' + TT('bagempty', 'Nothing yet. Tap a card, add it here.') + '</div>';
        tot.innerHTML = ''; measure(); return;
      }
      var html = '';
      for (var i = 0; i < CART.items.length; i++) html += lineHTML(CART.items[i]);
      lines.innerHTML = html;
      rowEl = null; openRow = null;
      var sp = giftSplit(CART);
      var needbq = giftNeedsBouquet(CART);
      bag.classList.toggle('needbq', needbq);
      if (sp.gift) {
        tot.innerHTML = giftTotalsHTML(CART) + (needbq ? needBouquetHTML() : '');
      } else {
        var flowers = CART.total_price;
        var frac = Math.min(flowers / FREE_AT_MINOR, 1);
        tot.innerHTML = totalsHTML(flowers);
        progSp.jump(Math.min(progSp.x, frac)); progSp.set(frac, { damping: 1, response: 0.5 });
      }
      measure();
    }

    function tellNav(open) {
      document.dispatchEvent(new CustomEvent('fluidbag', { detail: { open: open } }));
    }
    function openBag() {
      /* settle the cash-gift fee BEFORE painting, so the total the bag shows
         is the total checkout will charge (the delivery fold happens here,
         not at the last moment) */
      refreshCart().then(function (c0) {
        return giftFeeSettle(c0).then(function (c1) {
          if (c1 && c1.items) { CART = c1; setCount(c1.item_count); }
        }).catch(function () {});
      }).then(function () {
        render();
        bag.classList.add('up');
        requestAnimationFrame(function () {
          measure(); lockScroll(); tellNav(true);
          bagSp.set(0, { damping: damp(), response: 0.38 });
        });
      });
    }
    function closeBag() {
      measure(); tellNav(false);
      bagSp.set(1, { damping: 1, response: 0.32, onRest: function () { bag.classList.remove('up'); unlockScroll(); } });
    }
    window.fluidOpenBag = openBag;
    var openBtn = document.querySelector('.fluid-bagbtn');
    if (openBtn) openBtn.addEventListener('click', openBag);
    bscrim.addEventListener('click', closeBag);

    bag.addEventListener('click', function (e) {
      var chk = e.target.closest('.fluid-checkout');
      if (chk) return;   /* the order-details sheet takes it from here */
      /* one cart write at a time: two fast taps used to fire two changes
         computed off the same stale quantity (27 Aug sweep). The busy class
         was already set during flight - it just never blocked anything. */
      if (bag.classList.contains('busy')) return;
      var del = e.target.closest('[data-del]');
      if (del) {
        buzz(9);
        bag.classList.add('busy');
        fetch('/cart/change.js', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: del.getAttribute('data-del'), quantity: 0 })
        }).then(function (r) { return r.json(); }).then(function (c) {
          CART = c; setCount(c.item_count); render(); bag.classList.remove('busy');
        }).catch(function () { bag.classList.remove('busy'); });
        return;
      }
      var btn = e.target.closest('[data-k]'); if (!btn) return;
      var it = null;
      for (var i = 0; i < CART.items.length; i++) if (CART.items[i].key === btn.getAttribute('data-k')) it = CART.items[i];
      if (!it) return;
      buzz(5);
      var q = it.quantity + parseInt(btn.getAttribute('data-d'), 10);
      bag.classList.add('busy');
      fetch('/cart/change.js', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: it.key, quantity: Math.max(0, q) })
      }).then(function (r) { return r.json(); }).then(function (c) {
        CART = c; setCount(c.item_count); render(); bag.classList.remove('busy');
      }).catch(function () { bag.classList.remove('busy'); });
    });

    /* swipe a line left: Delete waits underneath (the Apple Store cart gesture) */
    var rowEl = null, openRow = null;
    var lines = bag.querySelector('.fluid-blines');
    var rowSp = new Spring(0, function (x) {
      if (rowEl) rowEl.style.transform = 'translate3d(' + x + 'px,0,0)';
    });
    var rdrag = null; var rtr = new Tracker();
    if (lines) {
      lines.addEventListener('pointerdown', function (e) {
        if (e.target.closest('button')) return;
        var row = e.target.closest('.fluid-bline'); if (!row) return;
        if (rowEl && rowEl !== row) { rowSp.stop(); rowEl.style.transform = ''; openRow = null; rowSp.jump(0); }
        if (rowEl !== row) { rowEl = row; rowSp.jump(0); }
        rowSp.stop();
        rdrag = { x: e.clientX, y: e.clientY, base: rowSp.x, axis: null };
        rtr.clear(); rtr.add(e.clientX);
        lines.setPointerCapture(e.pointerId);
      });
      lines.addEventListener('pointermove', function (e) {
        if (!rdrag) return;
        var dx = e.clientX - rdrag.x, dy = e.clientY - rdrag.y;
        if (!rdrag.axis) {
          if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
          rdrag.axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
          if (rdrag.axis === 'y') { rdrag = null; return; }
        }
        e.preventDefault(); rtr.add(e.clientX);
        var x = rdrag.base + dx;
        if (x > 0) x = rubber(x, 200);
        if (x < -88) x = -88 + rubber(x + 88, 200);
        rowSp.jump(x);
      });
      var rowEnd = function () {
        if (!rdrag) return;
        var was = rdrag.axis === 'x'; rdrag = null;
        if (!was || !rowEl) return;
        var v = rtr.velocity();
        var open = v < -250 ? true : v > 250 ? false : rowSp.x < -44;
        if (open !== (openRow === rowEl)) buzz(6);
        rowSp.set(open ? -88 : 0, { velocity: v, damping: open ? damp() : 1, response: 0.35 });
        openRow = open ? rowEl : null;
      };
      lines.addEventListener('pointerup', rowEnd);
      lines.addEventListener('pointercancel', rowEnd);
    }

    var drag = null; var tr = new Tracker();
    bag.addEventListener('pointerdown', function (e) {
      if (e.target.closest('button, a, .fluid-bline')) return;
      measure(); bag.setPointerCapture(e.pointerId);
      bagSp.stop(); drag = { y: e.clientY, base: bagSp.x }; tr.clear(); tr.add(e.clientY);
    });
    bag.addEventListener('pointermove', function (e) {
      if (!drag) return; tr.add(e.clientY);
      var dy = e.clientY - drag.y;
      var t = drag.base + dy / bagH;
      if (t < 0) t = -rubber(-t * bagH, bagH) / bagH;
      bagSp.jump(t);
    });
    function end() {
      if (!drag) return;
      var vy = tr.velocity(); drag = null;
      var vt = vy / bagH, close;
      if (Math.abs(vy) > 320) close = vy > 0; else close = bagSp.x > 0.4;
      if (close) { buzz(9); tellNav(false); bagSp.set(1, { velocity: vt, damping: 1, response: 0.32, onRest: function () { bag.classList.remove('up'); unlockScroll(); } }); }
      else bagSp.set(0, { velocity: vt, damping: damp(), response: 0.38 });
    }
    bag.addEventListener('pointerup', end);
    bag.addEventListener('pointercancel', end);
  })();

  refreshCart();

  /* ---------------- the tab bar: capsule springs between pages ----------- */
  (function () {
    var nav = document.querySelector('.fluid-nav'); if (!nav) return;
    var cap = nav.querySelector('.fluid-nav-cap');
    var items = nav.querySelectorAll('.fluid-nav-item');
    var current = '', prev = '';
    var capX = -200, capS = 1;
    function capDraw() {
      cap.style.transform = 'translate3d(' + capX + 'px,-50%,0) scale(' + capS + ')';
    }
    var cx = new Spring(-200, function (x) { capX = x; capDraw(); });
    var cs = new Spring(1, function (s) { capS = s; capDraw(); });
    var cw = new Spring(80, function (w) { cap.style.width = w + 'px'; });
    function byName(name) {
      for (var i = 0; i < items.length; i++)
        if (items[i].getAttribute('data-nav') === name) return items[i];
      return null;
    }
    /* the tap pop: capsule and icon overshoot as the selection lands */
    var iconEl = null;
    var iconSp = new Spring(1, function (s) {
      if (iconEl) iconEl.style.transform = 'scale(' + s + ')';
    });
    function pop(el) {
      if (REDUCE.matches) return;
      var ico = el.querySelector('.ico') || el;
      if (iconEl && iconEl !== ico) iconEl.style.transform = '';
      iconEl = ico;
      iconSp.jump(0.8);
      iconSp.set(1, { velocity: 5, damping: 0.5, response: 0.34 });
      cs.jump(0.9);
      cs.set(1, { velocity: 2.5, damping: 0.55, response: 0.36 });
    }
    function place(el, instant) {
      if (instant || REDUCE.matches) { cx.jump(el.offsetLeft); cw.jump(el.offsetWidth); cs.jump(1); return; }
      cx.set(el.offsetLeft, { damping: damp(), response: 0.42 });
      cw.set(el.offsetWidth, { damping: 1, response: 0.42 });
      buzz(6);
    }
    function setActive(name, instant) {
      if (name === current) return;
      prev = current; current = name;
      for (var i = 0; i < items.length; i++)
        items[i].classList.toggle('is-active', items[i].getAttribute('data-nav') === name);
      var el = byName(name);
      if (el) place(el, instant);
    }
    requestAnimationFrame(function () { setActive('home', true); });
    addEventListener('resize', function () {
      var el = byName(current); if (el) { cx.jump(el.offsetLeft); cw.jump(el.offsetWidth); }
    });

    /* iOS: the keyboard must never bury the bar - ride the visual viewport */
    if (window.visualViewport) (function () {
      var vv = visualViewport, kraf = false;
      function kplace() {
        kraf = false;
        if (!kbFocused()) { nav.style.bottom = ''; return; }
        var off = Math.max(0, innerHeight - vv.height - vv.offsetTop);
        nav.style.bottom = off > 1 ? (off + 10) + 'px' : '';
      }
      function kreq() { if (!kraf) { kraf = true; requestAnimationFrame(kplace); } }
      vv.addEventListener('resize', kreq);
      vv.addEventListener('scroll', kreq);
      document.addEventListener('focusin', kreq);
      document.addEventListener('focusout', kreq);
    })();

    /* the bar ducks away on scroll down, springs back on scroll up */
    var hidden = false, lastY = scrollY, acc = 0, guardUntil = 0;
    var ny = new Spring(0, function (y) { nav.style.transform = 'translate3d(0,' + y + 'px,0)'; });
    function setHidden(h, instant) {
      if (h === hidden) return; hidden = h; acc = 0;
      if (!h) nav.classList.remove('gone');
      if (instant || REDUCE.matches) { ny.jump(h ? 140 : 0); nav.classList.toggle('gone', h); return; }
      ny.set(h ? 140 : 0, { damping: h ? 1 : 0.82, response: h ? 0.5 : 0.45,
        onRest: h ? function () { if (hidden) nav.classList.add('gone'); } : null });
    }
    addEventListener('scroll', function () {
      var raw = scrollY;
      var smax = Math.max(0, document.documentElement.scrollHeight - innerHeight);
      var y = Math.max(0, Math.min(raw, smax));
      var d = y - lastY; lastY = y;
      if (raw < 0 || raw > smax) return;      /* iOS rubber band, not a gesture */
      if (current === 'bag' || performance.now() < guardUntil) return;
      if (Math.abs(d) > 200) return;            /* programmatic jump, not a gesture */
      if (y < 70) { setHidden(false); return; }
      if (d > 0) { acc = Math.max(0, acc) + d; if (acc > 26) setHidden(true); }
      else if (d < 0) { acc = Math.min(0, acc) + d; if (acc < -10) setHidden(false); }
    }, { passive: true });

    nav.addEventListener('click', function (e) {
      var it = e.target.closest('.fluid-nav-item'); if (!it) return;
      var name = it.getAttribute('data-nav');
      guardUntil = performance.now() + 1100; setHidden(false);
      pop(it);
      if (name === 'bag') {
        e.preventDefault();
        if (window.fluidOpenBag) window.fluidOpenBag();
        return;
      }
      if (name === 'custom') {
        e.preventDefault();
        if (window.fluidOpenCustom) window.fluidOpenCustom();
        return;
      }
      if (home && name === 'home') {
        e.preventDefault();
        setActive('home');
        scrollTo({ top: 0, behavior: REDUCE.matches ? 'auto' : 'smooth' });
      }
      /* everything else is a real link and navigates */
    });

    /* a browse page tells the bar which tab it belongs to */
    document.addEventListener('fluidtab', function (e) {
      if (e.detail && e.detail.name) setActive(e.detail.name);
    });

    /* the bag and the custom sheet borrow the highlight while up */
    document.addEventListener('fluidbag', function (e) {
      if (e.detail.open) { setActive(e.detail.name || 'bag'); setHidden(false, true); }
      else setActive(home ? 'home' : (prev && prev !== 'bag' && prev !== 'custom' ? prev : 'home'));
    });

    /* search: one plain bar (CEO, 22 Aug). Tap the circle and a layer rises
       carrying its OWN field at the top - the keyboard comes up over it and
       recommendations render as they type. The tab bar steps aside entirely
       (visibility, via .searching) instead of morphing around a bottom
       field the browser chrome kept fighting. */
    var searchEl = document.querySelector('.fluid-nav-search');
    if (searchEl) (function () {
      var sgo = searchEl.querySelector('.sgo');
      if (!sgo) return;
      var searching = false, layer = null, deb = 0;
      function imgW(u, w) { return u ? u + (u.indexOf('?') > -1 ? '&' : '?') + 'width=' + w : ''; }
      function esc(s) { return String(s || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/"/g, '&quot;'); }
      function card(p) {
        var raw = (p.featured_image && p.featured_image.url) || p.image || '';
        var img = imgW(raw, 500);
        var price = p.price ? ((String(p.price).indexOf('KD') > -1 || String(p.price).indexOf('د.ك') > -1) ? p.price : p.price + KD_UNIT) : '';
        return '<button class="fluid-gcard"' +
          ' data-handle="' + esc(p.handle) + '" data-title="' + esc(p.title) + '"' +
          ' data-price="' + esc(price) + '" data-img="' + esc(img) + '" data-amb="' + esc(imgW(raw, 64)) + '"' +
          ' data-available="' + (p.available === false ? 'false' : 'true') + '">' +
          '<img class="shot" src="' + esc(img) + '" alt="" loading="lazy">' +
          '<div class="veil"></div>' +
          '<div class="inner"><div class="fluid-gname">' + esc(p.title) + '</div>' +
          '<div class="fluid-gprice">' + esc(price) + '</div></div></button>';
      }
      /* the keyword line: what they are searching, pinned under the bar */
      function setKw(q, note) {
        var kw = layer && layer.querySelector('.skw');
        if (!kw) return;
        if (!q) { kw.className = 'skw'; kw.innerHTML = ''; return; }
        kw.className = 'skw on';
        kw.innerHTML = '<b>' + esc(q) + '</b><span>' + esc(note || '') + '</span>';
      }
      function count(n) {
        if (!n) return 'nothing found';
        return n === 1 ? '1 result' : n + ' results';
      }
      function run(q) {
        var res = layer && layer.querySelector('.sres');
        if (!res) return;
        if (!q || q.length < 2) { res.innerHTML = '<div class="snone">Type a flower, a colour, or an occasion.</div>'; return; }
        fetch('/search/suggest.json?q=' + encodeURIComponent(q) + '&resources[type]=product&resources[limit]=8')
          .then(function (r) { return r.json(); })
          .then(function (j) {
            if (!layer) return;
            var cur = layer.querySelector('.sfield');
            if (cur && cur.value.trim() !== q) return;   /* a later keystroke owns the page */
            var ps = (((j || {}).resources || {}).results || {}).products || [];
            setKw(q, count(ps.length));
            res.innerHTML = ps.length ? ps.map(card).join('') :
              '<div class="snone">Nothing for "' + esc(q) + '" - try an occasion or a flower.</div>';
          }).catch(function () {});
      }
      function openSearch() {
        if (searching) return; searching = true;
        buzz(8); lockScroll();
        nav.classList.add('searching');
        var el = document.createElement('div');
        el.className = 'fluid-srch';
        el.innerHTML = '<div class="shead">' +
          '<input class="sfield" type="search" placeholder="Search flowers, occasions" autocomplete="off" aria-label="Search">' +
          '<button class="scancel" type="button">Cancel</button></div>' +
          '<div class="skw" aria-live="polite"></div>' +
          '<div class="sbody"><div class="fluid-grid sres">' +
          '<div class="snone">Type a flower, a colour, or an occasion.</div></div></div>';
        document.body.appendChild(el);
        layer = el;
        var input = el.querySelector('.sfield');
        var rise = new Spring(1, function (t) { el.style.transform = 'translate3d(0,' + (t * 102) + '%,0)'; });
        rise.jump(1);
        rise.set(0, { damping: 1, response: 0.5 });
        el._rise = rise;

        /* THE FIX (round 43). iOS raises the keyboard by scrolling the
           VISUAL viewport inside the layout viewport. A position:fixed
           layer is glued to the LAYOUT viewport, so the whole search page
           slides up off the top of the screen and has to be dragged back
           down by hand - the CEO's exact report. Sizing the layer to the
           visual viewport while the keyboard is up keeps it where it can
           actually be seen. Scoped to this layer and to a REAL keyboard:
           the 90px floor keeps Safari's own collapsing browser bars out of
           it, which is what parked the tab bar mid-screen in the 22 Aug
           video when chrome rode the visual viewport unconditionally. */
        var vv = window.visualViewport;
        function vvfit() {
          if (!layer || !vv) return;
          if (kbFocused() && (innerHeight - vv.height) > 90) {
            layer.style.top = Math.round(vv.offsetTop) + 'px';
            layer.style.bottom = 'auto';
            layer.style.height = Math.round(vv.height) + 'px';
          } else {
            layer.style.top = ''; layer.style.bottom = ''; layer.style.height = '';
          }
        }
        if (vv) {
          vv.addEventListener('resize', vvfit);
          vv.addEventListener('scroll', vvfit);
          el._vvoff = function () {
            vv.removeEventListener('resize', vvfit);
            vv.removeEventListener('scroll', vvfit);
          };
        }
        el.addEventListener('click', function (e) {
          if (e.target.closest('.scancel')) { closeSearch(); return; }
          var c = e.target.closest('.fluid-gcard');
          if (c) {
            if (home) { input.blur(); sheetFromCard(c); }
            else if (c.getAttribute('data-handle')) location.href = '/products/' + c.getAttribute('data-handle');
          }
        });
        input.addEventListener('input', function () {
          var v = input.value.trim();
          /* the keyword answers the keystroke; the results answer the pause */
          setKw(v, v.length < 2 ? 'keep typing' : 'looking');
          var body = el.querySelector('.sbody');
          if (body) body.scrollTop = 0;
          clearTimeout(deb);
          deb = setTimeout(function () { run(v); }, 250);
        });
        input.addEventListener('keydown', function (e) {
          if (e.key === 'Enter') { clearTimeout(deb); run(input.value.trim()); input.blur(); }
        });
        /* focus INSIDE the tap gesture, so iOS actually raises the keyboard */
        try { input.focus(); } catch (err) {}
        /* the keyboard animates in, so fit again once it has landed */
        setTimeout(vvfit, 320); setTimeout(vvfit, 700);
        srchClose = closeSearch;
      }
      function closeSearch() {
        if (!searching) return; searching = false;
        srchClose = null;
        buzz(8);
        nav.classList.remove('searching');
        var el = layer; layer = null;
        var inp = el.querySelector('.sfield');
        if (inp) inp.blur();
        if (el._vvoff) { el._vvoff(); el._vvoff = null; }
        el.style.top = ''; el.style.bottom = ''; el.style.height = '';
        el._rise.set(1, { damping: 1, response: 0.4, onRest: function () { el.remove(); unlockScroll(); } });
      }
      sgo.addEventListener('click', function (e) {
        e.preventDefault();
        if (!searching) openSearch();
      });
      var pillx = nav.querySelector('.pillx');
      if (pillx) pillx.addEventListener('click', function () { closeSearch(); });
    })();
  })();

  /* ---------------- order details: what a florist actually needs ----------
     The bag knows what is being sent. This sheet asks when, to whom, and what
     the card should say, then writes the SAME cart attributes the live
     checkout already collects — so a Facelift order reaches the workroom in
     the shape the team reads today (Delivery Date / Day / Time, Gift,
     Surprise, Recipient name and phone, Order Note). ---------------------- */
  (function () {
    var TIMES = [
      { v: '12:00 PM - 3:00 PM', end: 15 },
      { v: '3:00 PM - 6:00 PM', end: 18 },
      { v: '6:00 PM - 11:45 PM', end: 23.75 }
    ];
    var CUTOFF = 21;                 /* 9pm Kuwait, hard (house rule) */
    var DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    var SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    var MONS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    var MSG_MAX = 240;
    /* 28 Aug: the sheet speaks the customer's language. Display strings come
       through the data-t-* bridge on .fluid-bag; the ATTRIBUTE VALUES the
       order carries (Delivery Date, Day, Time) stay English - they are the
       staff's records and the prefill matches against them. */
    var ORD_AR = (document.documentElement.lang || '').indexOf('ar') === 0;
    function tlist(name, fb) {
      var v = TT(name, '');
      if (!v) return fb;
      var a = v.split(',');
      return a.length === fb.length ? a : fb;
    }
    var SHORT_T = tlist('orddays', SHORT);
    var MONS_T = tlist('ordmons', MONS);
    var TIMES_T = tlist('ordtimes', [TIMES[0].v, TIMES[1].v, TIMES[2].v]);

    /* Kuwait sits at UTC+3 all year, so the cutoff needs no timezone table */
    function kNow() { var n = new Date(); return new Date(n.getTime() + (n.getTimezoneOffset() + 180) * 60000); }
    function fmtDate(d) { return MONS[d.getMonth()] + ' ' + d.getDate() + ', ' + d.getFullYear(); }
    function esc(t) {
      return String(t == null ? '' : t)
        .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
    }
    function digits(t) { return String(t || '').replace(/[^0-9]/g, ''); }

    var layer = null, oscrim = null, rise = null, pick = null, adds = [];

    /* the card-words helper (CEO, 21 Aug): a feeling, a suggestion, a card.
       Curated lines, no apostrophes (house style), rotated per tap.
       Round 36 (CEO): TEN lines per feeling, in different voices - plain,
       warm, playful, poetic, formal for elders and colleagues, and a
       little Kuwait - so every kind of customer finds one that sounds
       like them. */
    var BANK = {
      'Love': [
        'Every day with you is my favourite day. All my love.',
        'A hundred flowers, and not one as lovely as you.',
        'For the one who makes ordinary days feel like occasions.',
        'Still my best decision. Always.',
        'No reason. Just you.',
        'Home is wherever you are.',
        'I checked with the florist. They do not make one prettier than you.',
        'Loving you is the easiest thing I have ever done.',
        'To my whole heart, from all of mine.',
        'Ten years from now I will still be sending you flowers.'
      ],
      'Birthday': [
        'Happy birthday. May this year be your softest and sweetest yet.',
        'Another year of you is worth celebrating. Happy birthday.',
        'Happy birthday. The flowers are pretty, but you are prettier.',
        'Happy birthday. Here is to you, today and every day.',
        'It is your day. Let everyone know.',
        'Happy birthday to someone who only gets better.',
        'May your new year be full of everything you love.',
        'A year older, somehow lovelier. Happy birthday.',
        'Wishing you a beautiful year ahead. Happy birthday.',
        'Blow out the candles. The flowers are already here.'
      ],
      'Thank you': [
        'Thank you for everything you do, seen and unseen.',
        'Some kindness cannot be repaid, only remembered. Thank you.',
        'A small thank you for a very big heart.',
        'Thank you. Truly, deeply, honestly.',
        'You showed up when it mattered. I will not forget it.',
        'For all the things you did without being asked. Thank you.',
        'Words felt too small, so I sent flowers.',
        'Thank you for being exactly who you are.',
        'With sincere thanks and much appreciation.',
        'You made a hard day easier. Thank you.'
      ],
      'Congratulations': [
        'Congratulations. Nobody deserves this more than you.',
        'You worked for this, and it shows. So proud of you.',
        'Mabrook! To new beginnings and everything good ahead.',
        'Alf mabrook. May it be the first of many.',
        'This is your moment. Enjoy every second of it.',
        'We always knew you could. Congratulations.',
        'Congratulations on the new chapter. It suits you already.',
        'Big news deserves big flowers. Congratulations.',
        'With warmest congratulations on your achievement.',
        'You did it. Now celebrate properly.'
      ],
      'Get well': [
        'Some colour to keep you company while you rest. Get well soon.',
        'Rest well and heal fully. We are thinking of you.',
        'Flowers to hold your place until you are back on your feet.',
        'Salamat. May you be back to yourself very soon.',
        'Take all the rest you need. We will be here.',
        'Sending you brightness until you feel like yourself again.',
        'Get well soon. The world is better with you up and about.',
        'No rush. Just heal. We are all thinking of you.',
        'Wishing you a smooth and speedy recovery.',
        'A little sunshine for your bedside table.'
      ],
      'Missing you': [
        'Missing you today, and most days. These felt like you.',
        'Some distance cannot be helped. You are always close from the heart.',
        'Until we sit together again, let these do the talking.',
        'Thinking of you more than usual today.',
        'The distance is temporary. You and I are not.',
        'Sending flowers because a text felt too small.',
        'Same city or far away, you are always on my mind.',
        'Counting the days until I see you again.',
        'A little of home, delivered to you.',
        'Missed you enough to tell the whole flower shop about it.'
      ]
    };
    var BANKKEYS = ['Love', 'Birthday', 'Thank you', 'Congratulations', 'Get well', 'Missing you'];
    var msKey = '', msIx = 0;
    function bankChips() {
      var h = '';
      for (var i = 0; i < BANKKEYS.length; i++) {
        h += '<button type="button" class="mhk" data-msk="' + BANKKEYS[i] + '">' + BANKKEYS[i] + '</button>';
      }
      return h;
    }

    function dayList() {
      var now = kNow(), out = [], start = now.getHours() >= CUTOFF ? 1 : 0;
      for (var i = start; i < start + 14; i++) {
        var d = new Date(now.getFullYear(), now.getMonth(), now.getDate() + i);
        out.push(d);
      }
      return out;
    }
    function sameDay(a, b) {
      return a && b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
    }
    function isToday(d) { var n = kNow(); return sameDay(d, new Date(n.getFullYear(), n.getMonth(), n.getDate())); }

    /* a window that has already finished today cannot be chosen — arithmetic,
       not policy: the 9pm cutoff above is the policy */
    function timeOpen(t) {
      if (!pick.date || !isToday(pick.date)) return true;
      var n = kNow();
      return (n.getHours() + n.getMinutes() / 60) < t.end;
    }

    function money() {
      /* with a cash gift in the cart, Shopify free-ships at the CART total
         (the fold in giftFeeSettle keeps that honest); "delivery free" is
         only claimed when the flowers earn it on their own */
      var cartTotal = CART ? CART.total_price : 0;
      var sp = CART ? giftSplit(CART) : { gift: null, flowers: 0 };
      var flowers = sp.gift ? sp.flowers : cartTotal;
      var shipCharged = cartTotal < FREE_AT_MINOR;
      return {
        flowers: flowers,
        free: !shipCharged && flowers >= FREE_AT_MINOR,
        plain: !shipCharged && flowers < FREE_AT_MINOR,
        total: cartTotal + (shipCharged ? DELIVERY_MINOR : 0)
      };
    }

    function build() {
      var days = dayList();
      var h = '';
      h += '<div class="fluid-ohead">' +
        '<button class="fluid-oback" type="button" aria-label="' + TT('ordback', 'Back to bag') + '">' +
        '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M15 5 L8 12 L15 19"/></svg>' +
        '</button><h2>' + MARKLET + TT('ordtitle', 'Order details') + '</h2></div>';

      h += '<div class="fluid-obody">';

      h += '<section class="fluid-ogrp"><h3>' + TT('ordwhen', 'When should it arrive') + '</h3><div class="fluid-odays">';
      for (var i = 0; i < days.length; i++) {
        var d = days[i];
        h += '<button class="fluid-oday" type="button" data-d="' + i + '">' +
          '<span class="dw">' + (isToday(d) ? TT('ordtoday', 'Today') : SHORT_T[d.getDay()]) + '</span>' +
          '<span class="dn">' + d.getDate() + '</span>' +
          '<span class="dm">' + MONS_T[d.getMonth()] + '</span></button>';
      }
      h += '</div><p class="fluid-onote">' + TT('ordcutoff', 'Same day when you order by 9pm. After 9pm the earliest is tomorrow.') + '</p>' +
        '<p class="fluid-oerr" data-e="date">' + TT('orderrdate', 'Choose the day you want it delivered.') + '</p></section>';

      h += '<section class="fluid-ogrp"><h3>' + TT('ordtime', 'Time of day') + '</h3><div class="fluid-otimes">';
      for (var t = 0; t < TIMES.length; t++) {
        h += '<button class="fluid-otime" type="button" data-t="' + t + '"><span>' + TIMES_T[t] + '</span>' +
          '<svg class="tick" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12.5 L10 17.5 L19 7"/></svg>' +
          '</button>';
      }
      h += '</div><p class="fluid-oerr" data-e="time">' + TT('orderrtime', 'Choose a delivery time.') + '</p></section>';

      h += '<section class="fluid-ogrp"><h3>' + TT('ordcard', 'The card') + '</h3>' +
        '<textarea class="fluid-ofield" data-f="msg" rows="3" maxlength="' + MSG_MAX + '" ' +
        'placeholder="' + TT('ordcardph', 'Write what the card should say. Arabic is welcome.') + '"></textarea>' +
        '<div class="fluid-ocount"><span data-count>0</span> ' + TT('ordof', 'of') + ' ' + MSG_MAX + '</div>' +
        '<p class="fluid-onote">' + TT('ordcardnote', 'A printed card comes free with every order. Leave it empty for a blank card.') + '</p>' +
        /* the suggestion bank is English lines; on /ar it would put English
           words in the customer's mouth, so it does not render there. An
           Arabic bank is Um Content's to write. */
        (ORD_AR ? '' :
        '<div class="fluid-mhelp"><span class="mhl">Stuck for words? Tap a feeling and we suggest a line:</span>' +
        '<div class="mhchips">' + bankChips() + '</div></div>' +
        '<div class="fluid-msug" data-msug hidden><p data-msugtext></p>' +
        '<div class="msbtns"><button class="fluid-msuse" type="button" data-msuse>Use this</button>' +
        '<button class="fluid-msnext" type="button" data-msnext>Show another</button></div></div>') +
        '</section>';

      h += '<section class="fluid-ogrp"><h3>' + TT('ordwho', 'Who is it for') + '</h3>' +
        '<button class="fluid-oswitch" type="button" data-sw="gift"><span><span class="sl">' + TT('ordgift', 'This is a gift') + '</span>' +
        '<span class="sd">' + TT('ordgiftd', 'We deliver to someone else and keep the price off the card.') + '</span></span>' +
        '<span class="fluid-otrack"><span class="fluid-oknob"></span></span></button>' +
        '<div class="fluid-ogift" data-gift><div class="fluid-ogift-in">' +
        '<input class="fluid-ofield" data-f="name" type="text" autocomplete="name" placeholder="' + TT('ordnameph', 'Who is receiving it') + '">' +
        '<input class="fluid-ofield" data-f="phone" type="tel" inputmode="numeric" autocomplete="tel" placeholder="' + TT('ordphoneph', 'Their phone number') + '">' +
        '<p class="fluid-oerr" data-e="gift">' + TT('orderrgift', 'Add their name and an 8 digit Kuwait number so the driver can reach them.') + '</p>' +
        '<button class="fluid-oswitch" type="button" data-sw="surprise" style="margin-top:9px"><span><span class="sl">' + TT('ordsurprise', 'Keep it a surprise') + '</span>' +
        '<span class="sd">' + TT('ordsurprised', 'We will not say who it is from before it arrives.') + '</span></span>' +
        '<span class="fluid-otrack"><span class="fluid-oknob"></span></span></button>' +
        '<button class="fluid-oswitch" type="button" data-sw="call"><span><span class="sl">' + TT('ordcall', 'Call them for the address') + '</span>' +
        '<span class="sd">' + TT('ordcalld', 'Use this when you do not have their address.') + '</span></span>' +
        '<span class="fluid-otrack"><span class="fluid-oknob"></span></span></button>' +
        '<p class="fluid-onote ocall" data-callnote hidden>' + TT('ordcallnote', 'Put your own address at checkout — we will call them and take theirs, no worries.') + '</p>' +
        '</div></div></section>';

      h += '<section class="fluid-ogrp" data-addwrap hidden><h3>' + TT('ordadds', 'Finish the gift') + '</h3>' +
        '<div class="fluid-oadds" data-adds></div>' +
        '<p class="fluid-onote">' + TT('ordaddsnote', 'Toppers and chocolates ride along with the flowers.') + '</p></section>';

      h += '<section class="fluid-ogrp"><h3>' + TT('ordnote', 'Anything we should know') + '</h3>' +
        '<textarea class="fluid-ofield" data-f="note" rows="2" maxlength="500" ' +
        'placeholder="' + TT('ordnoteph', 'A landmark, a floor number, a time to avoid.') + '"></textarea></section>';

      h += '</div>';

      h += '<div class="fluid-ofoot"><div class="fluid-ototals"><span data-tlbl>' + TT('total', 'Total') + '</span><b data-tval></b></div>' +
        '<button class="fluid-ogo fluid-press" type="button">' + TT('ordgo', 'Continue to payment') + '</button>' +
        '<div class="fluid-ordpay" data-ordpay></div></div>';
      return h;
    }

    function paintDays() {
      var days = dayList(), b = layer.querySelectorAll('.fluid-oday');
      for (var i = 0; i < b.length; i++) b[i].classList.toggle('on', sameDay(days[i], pick.date));
    }
    function paintTimes() {
      var b = layer.querySelectorAll('.fluid-otime');
      for (var i = 0; i < b.length; i++) {
        var open = timeOpen(TIMES[i]);
        b[i].disabled = !open;
        b[i].classList.toggle('on', open && pick.time === TIMES[i].v);
        var lbl = b[i].querySelector('span');
        if (lbl) lbl.textContent = TIMES_T[i];
      }
      if (pick.time && !timeOpen({ end: TIMES[timeIndex(pick.time)].end })) pick.time = '';
    }
    function timeIndex(v) { for (var i = 0; i < TIMES.length; i++) if (TIMES[i].v === v) return i; return 0; }
    function paintTotals() {
      var m = money(), tv = layer.querySelector('[data-tval]'), tl = layer.querySelector('[data-tlbl]');
      if (tv) tv.textContent = KD(m.total);
      if (tl) tl.textContent = m.free ? TT('ordtotalfree', 'Total, delivery free')
        : (m.plain ? TT('total', 'Total') : TT('ordtotaldel', 'Total including 3 KD delivery'));
    }
    function paintSwitch(name) {
      var sw = layer.querySelector('[data-sw="' + name + '"]');
      if (!sw) return;
      sw.classList.toggle('on', !!pick[name]);
      if (name === 'call') {
        /* checkout will still demand an address (Shopify requires one for
           shippable goods), so the switch answers the question it creates:
           whose address to type. CEO asked for this line, 31 Aug. */
        var cnote = layer.querySelector('[data-callnote]');
        if (cnote) cnote.hidden = !pick.call;
      }
      var knob = sw.querySelector('.fluid-oknob');
      if (knob) {
        var to = pick[name] ? 20 : 0;
        if (REDUCE.matches) knob.style.transform = 'translate3d(' + to + 'px,0,0)';
        else { var s = new Spring(pick[name] ? 0 : 20, function (x) { knob.style.transform = 'translate3d(' + x + 'px,0,0)'; }); s.set(to, { damping: 0.78, response: 0.3 }); }
      }
    }
    function paintGift(instant) {
      var box = layer.querySelector('[data-gift]');
      if (!box) return;
      var inner = box.querySelector('.fluid-ogift-in');
      var target = pick.gift && inner ? inner.offsetHeight : 0;
      box.style.opacity = pick.gift ? '1' : '0';
      box.style.pointerEvents = pick.gift ? 'auto' : 'none';
      if (instant || REDUCE.matches) {
        box.style.height = target + 'px';
        if (box._sp) box._sp.jump(target);
        return;
      }
      if (!box._sp) box._sp = new Spring(0, function (hh) { box.style.height = hh + 'px'; });
      box._sp.set(target, { damping: 1, response: 0.42 });
    }
    function err(which, on) {
      var e = layer.querySelector('[data-e="' + which + '"]');
      if (e) e.classList.toggle('on', !!on);
    }

    function loadAdds() {
      var wrap = layer.querySelector('[data-adds]'), sect = layer.querySelector('[data-addwrap]');
      if (!wrap) return;
      Promise.all([
        fetch('/collections/toppers/products.json?limit=8').then(function (r) { return r.json(); }).catch(function () { return { products: [] }; }),
        fetch('/products/belgian-chocolate-box.js').then(function (r) { return r.ok ? r.json() : null; }).catch(function () { return null; })
      ]).then(function (res) {
        if (!layer) return;
        var list = (res[0] && res[0].products) ? res[0].products.slice(0, 8) : [];
        if (res[1]) list.unshift({ title: res[1].title, variants: res[1].variants, images: [res[1].featured_image] });
        adds = [];
        var html = '';
        for (var i = 0; i < list.length; i++) {
          var p = list[i];
          var vid = p.variants && p.variants[0] ? p.variants[0].id : 0;
          var img = '';
          if (p.images && p.images.length) img = typeof p.images[0] === 'string' ? p.images[0] : (p.images[0] && p.images[0].src) || '';
          if (!vid || !img) continue;
          var minor = p.variants[0].price;
          if (typeof minor === 'string') minor = Math.round(parseFloat(minor) * 100);
          adds.push(vid);
          html += '<button class="fluid-oadd" type="button" data-v="' + vid + '">' +
            '<span class="aplus">+</span>' +
            '<img src="' + esc(img) + (img.indexOf('?') > -1 ? '&' : '?') + 'width=240" alt="" loading="lazy">' +
            '<span class="an">' + esc(p.title) + '</span>' +
            '<span class="ap">' + KD(minor) + '</span></button>';
        }
        wrap.innerHTML = html;
        if (sect) sect.hidden = adds.length === 0;
      }).catch(function () {});
    }

    function prefill() {
      var a = (CART && CART.attributes) || {};
      pick = {
        date: null, time: a['Delivery Time'] || '',
        gift: a['Gift'] === 'Yes', surprise: a['Surprise'] === 'Yes',
        call: a['Call recipient for address'] === 'Yes',
        msg: a['Card message'] || '', name: a['Recipient name'] || '',
        phone: a['Recipient phone'] || '', note: a['Order Note'] || ''
      };
      var days = dayList();
      if (a['Delivery Date']) {
        for (var i = 0; i < days.length; i++) if (fmtDate(days[i]) === a['Delivery Date']) { pick.date = days[i]; break; }
      }
    }

    function open() {
      if (layer) return;
      prefill();
      msKey = ''; msIx = 0;
      oscrim = document.createElement('div');
      oscrim.className = 'fluid-oscrim';
      document.body.appendChild(oscrim);
      var el = document.createElement('div');
      el.className = 'fluid-ord';
      el.setAttribute('role', 'dialog');
      el.setAttribute('aria-label', 'Order details');
      el.innerHTML = build();
      document.body.appendChild(el);
      layer = el;
      lockScroll();

      /* capture the scrim locally: close() nulls the module var while the
         spring is still settling (the pass-8 lesson, third sighting) */
      var scEl = oscrim;
      rise = new Spring(102, function (y) {
        if (!el.isConnected) return;
        el.style.transform = 'translate3d(0,' + y + '%,0)';
        scEl.style.opacity = String(Math.max(0, 1 - y / 102));
      });
      rise.set(0, { damping: 1, response: 0.58 });

      var msg = el.querySelector('[data-f="msg"]');
      if (msg) { msg.value = pick.msg; countMsg(); }
      var nm = el.querySelector('[data-f="name"]'); if (nm) nm.value = pick.name;
      var ph = el.querySelector('[data-f="phone"]'); if (ph) ph.value = pick.phone;
      var nt = el.querySelector('[data-f="note"]'); if (nt) nt.value = pick.note;

      paintDays(); paintTimes(); paintTotals();
      paintSwitch('gift'); paintSwitch('surprise'); paintSwitch('call'); paintGift(true);
      loadAdds();
      var payT = document.getElementById('fl-pay');
      var payD = el.querySelector('[data-ordpay]');
      if (payT && payT.content && payD) payD.appendChild(payT.content.cloneNode(true));
      ordClose = close;

      el.addEventListener('click', onTap);
      el.addEventListener('input', onInput);
      keyboardWatch(el);
    }

    function countMsg() {
      var msg = layer.querySelector('[data-f="msg"]'), c = layer.querySelector('[data-count]');
      if (msg && c) c.textContent = String(msg.value.length);
    }

    function onInput(e) {
      var f = e.target.getAttribute && e.target.getAttribute('data-f');
      if (!f) return;
      pick[f] = e.target.value;
      if (f === 'msg') countMsg();
      if (f === 'name' || f === 'phone') err('gift', false);
    }

    function onTap(e) {
      var back = e.target.closest('.fluid-oback');
      if (back) { close(); return; }

      var day = e.target.closest('.fluid-oday');
      if (day) {
        pick.date = dayList()[parseInt(day.getAttribute('data-d'), 10)];
        buzz(6); err('date', false); paintDays(); paintTimes();
        return;
      }
      var tm = e.target.closest('.fluid-otime');
      if (tm && !tm.disabled) {
        pick.time = TIMES[parseInt(tm.getAttribute('data-t'), 10)].v;
        buzz(6); err('time', false); paintTimes();
        return;
      }
      var sw = e.target.closest('[data-sw]');
      if (sw) {
        var n = sw.getAttribute('data-sw');
        pick[n] = !pick[n];
        buzz(6); paintSwitch(n);
        if (n === 'gift' || n === 'call') paintGift();
        return;
      }
      var mk = e.target.closest('[data-msk]');
      if (mk) {
        buzz(6);
        var kk = mk.getAttribute('data-msk');
        if (msKey === kk) msIx = (msIx + 1) % BANK[kk].length;
        else { msKey = kk; msIx = 0; }
        var alls = layer.querySelectorAll('[data-msk]');
        for (var zi = 0; zi < alls.length; zi++) alls[zi].classList.toggle('on', alls[zi] === mk);
        var sug = layer.querySelector('[data-msug]');
        var stx = layer.querySelector('[data-msugtext]');
        if (sug && stx) { sug.hidden = false; stx.textContent = BANK[msKey][msIx]; }
        return;
      }
      if (e.target.closest('[data-msnext]')) {
        buzz(5);
        if (msKey) {
          msIx = (msIx + 1) % BANK[msKey].length;
          var stx2 = layer.querySelector('[data-msugtext]');
          if (stx2) stx2.textContent = BANK[msKey][msIx];
        }
        return;
      }
      if (e.target.closest('[data-msuse]')) {
        buzz(8);
        var stx3 = layer.querySelector('[data-msugtext]');
        var ta = layer.querySelector('[data-f="msg"]');
        if (stx3 && ta) {
          ta.value = stx3.textContent;
          pick.msg = ta.value;
          countMsg();
        }
        var sug3 = layer.querySelector('[data-msug]');
        if (sug3) sug3.hidden = true;
        var alls3 = layer.querySelectorAll('[data-msk]');
        for (var z3 = 0; z3 < alls3.length; z3++) alls3[z3].classList.remove('on');
        return;
      }
      var ad = e.target.closest('.fluid-oadd');
      if (ad) { addOn(ad); return; }
      var go = e.target.closest('.fluid-ogo');
      if (go) submit(go);
    }

    function addOn(btn) {
      if (btn.disabled) return;
      btn.disabled = true;
      var vid = parseInt(btn.getAttribute('data-v'), 10);
      if (btn.classList.contains('in')) {
        /* the second tap is an UNDO: take one unit of this add-on back out.
           The x used to run the add again, so a customer undoing a chocolate
           box was charged for two (27 Aug sweep). */
        function resetTile() {
          btn.classList.remove('in');
          var pl = btn.querySelector('.aplus');
          if (pl) pl.textContent = '+';
          btn.disabled = false;
        }
        fetch('/cart.js').then(function (r) { return r.json(); }).then(function (cart) {
          var line = null;
          for (var li = 0; li < cart.items.length; li++) {
            if (cart.items[li].variant_id === vid) { line = cart.items[li]; break; }
          }
          if (!line) return null;
          return fetch('/cart/change.js', {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: line.key, quantity: line.quantity - 1 })
          });
        }).then(function (r) {
          if (r && !r.ok) throw new Error('change failed');
          return refreshCart();
        }).then(function () {
          buzz(8); resetTile(); paintTotals();
        }).catch(function () {
          /* could not remove - leave the tile marked so the truth (it is
             still in the cart) stays visible, and free the button */
          btn.disabled = false;
        });
        return;
      }
      fetch('/cart/add.js', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: vid, quantity: 1 }] })
      }).then(function (r) {
        if (!r.ok) throw new Error('add failed');
        return refreshCart();
      }).then(function () {
        buzz(10);
        btn.classList.add('in');
        var plus = btn.querySelector('.aplus');
        if (plus) plus.textContent = 'x';
        paintTotals();
        btn.disabled = false;
      }).catch(function () { btn.disabled = false; });
    }

    function submit(go) {
      var ok = true;
      if (!pick.date) { err('date', true); ok = false; }
      if (!pick.time) { err('time', true); ok = false; }
      if (pick.gift && (!String(pick.name).trim() || digits(pick.phone).length < 8)) { err('gift', true); ok = false; }
      if (!ok) {
        var first = layer.querySelector('.fluid-oerr.on');
        if (first) first.scrollIntoView({ behavior: REDUCE.matches ? 'auto' : 'smooth', block: 'center' });
        buzz(18);
        return;
      }
      go.disabled = true;
      go.textContent = TT('wait', 'One moment');
      var attrs = {
        'Delivery Method': 'Delivery',
        'Delivery Date': fmtDate(pick.date),
        'Delivery Day': DAYS[pick.date.getDay()],
        'Delivery Time': pick.time,
        'Customer TimeZone': 'Asia/Kuwait',
        'Card message': String(pick.msg || '').trim(),
        'Gift': pick.gift ? 'Yes' : 'No',
        'Surprise': pick.gift && pick.surprise ? 'Yes' : 'No',
        'Call recipient for address': pick.gift && pick.call ? 'Yes' : 'No',
        'Recipient name': pick.gift ? String(pick.name).trim() : '',
        'Recipient phone': pick.gift ? digits(pick.phone) : '',
        'Order Note': String(pick.note || '').trim()
      };
      /* the sheet can outlive the cart it opened on (lines deleted from the
         bag, add-ons added here) - re-check the cash-gift rule and re-settle
         the fee against the FINAL cart before anything is saved */
      refreshCart().then(function (cfin) {
        if (giftNeedsBouquet(cfin)) {
          go.disabled = false;
          go.textContent = TT('ordgo', 'Continue to payment');
          close();
          if (window.fluidOpenBag) window.fluidOpenBag();
          return null;
        }
        return giftFeeSettle(cfin).then(function () {
          return fetch('/cart/update.js', {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            /* note: is the packing slip. The shop's packing-slip template can
               print exactly ONE free-text block - order.note - so the card
               message MUST travel there or the florist packs a blank card.
               See snippets/otf-card-message.liquid on the live theme: orders
               #2232 and #2233 both lost their paid-for messages to this exact
               miss. The 'Card message' attribute stays as the admin copy. */
            body: JSON.stringify({ note: String(pick.msg || '').trim(), attributes: attrs })
          });
        }).then(function (r) {
          if (!r.ok) throw new Error('attributes failed');
          location.href = '/checkout';
        });
      }).catch(function () {
        go.disabled = false;
        go.textContent = TT('ordgo', 'Continue to payment');
        err('date', false);
        var e = layer.querySelector('[data-e="time"]');
        if (e) { e.textContent = TT('orderrsave', 'That did not save. Check your connection and try again.'); e.classList.add('on'); }
      });
    }

    /* iOS lifts a keyboard over fixed chrome and reports it only through the
       visual viewport. Shrink the sheet to what is actually visible so the
       foot button stays reachable while typing. */
    function keyboardWatch(el) {
      var vv = window.visualViewport;
      if (!vv) return;
      var tick = false;
      function place() {
        tick = false;
        if (!el.isConnected) return;
        if (!kbFocused()) { el.style.bottom = ''; return; }
        var off = Math.max(0, innerHeight - vv.height - vv.offsetTop);
        el.style.bottom = off > 1 ? off + 'px' : '';
      }
      function ping() { if (tick) return; tick = true; requestAnimationFrame(place); }
      vv.addEventListener('resize', ping);
      vv.addEventListener('scroll', ping);
      el._kbOff = function () { vv.removeEventListener('resize', ping); vv.removeEventListener('scroll', ping); };
    }

    function close() {
      if (!layer) return;
      var el = layer, sc = oscrim;
      if (el._kbOff) el._kbOff();
      el.style.bottom = '';
      layer = null; oscrim = null; ordClose = null;
      rise.set(102, {
        damping: 1, response: 0.44, onRest: function () {
          el.remove(); if (sc) sc.remove(); unlockScroll();
        }
      });
    }

    /* the one way in: the bag's checkout button, and the cart page's button */
    document.addEventListener('click', function (e) {
      var c = e.target.closest('.fluid-checkout, [data-openorder]');
      if (!c) return;
      e.preventDefault();
      function proceed(cart) {
        if (!cart || cart.item_count === 0) return;
        if (giftNeedsBouquet(cart)) {
          /* rule 1: a cash gift never checks out alone. The bag carries the
             explanation and the way forward. */
          buzz(18);
          if (window.fluidOpenBag) window.fluidOpenBag();
          return;
        }
        giftFeeSettle(cart).then(function (c3) {
          if (c3 && c3.items) { CART = c3; setCount(c3.item_count); }
          open();
        }).catch(function () { open(); });
      }
      if (CART) { refreshCart().then(proceed).catch(function () { proceed(CART); }); return; }
      refreshCart().then(proceed).catch(function () {});
    });
  })();

  /* ---------------- the flow layer: custom bouquet and event styling ------
     CEO, 21 Aug: the custom flow shows the same way as the order details
     page, carrying every function the live page had - reference photos with
     spotted colours, moods that set a palette, the practicals, a live
     florist recap - and the events page gets a matching flow. One
     full-screen layer in the ord grammar, two builds. WhatsApp carries the
     brief; photos are attached by the customer in WhatsApp itself, because
     a wa.me link cannot carry an image. */
  (function () {
    var WA = '96599548711', MAXPH = 4;
    var MOODS = [
      { n: 'Warm sunset', p: ['#e8956d', '#d4623f', '#f2c98e', '#f7e2c0'] },
      { n: 'Cottage garden', p: ['#d98ba3', '#b1c48a', '#e7d3e0', '#f3e6cf'] },
      { n: 'Cream & blush', p: ['#f2d9d0', '#e5b8ad', '#f7ecdc', '#cbb9a4'] },
      { n: 'Wild & green', p: ['#7a8a5e', '#a9b98a', '#e4e8cf', '#f1ead9'] },
      { n: 'Bold jewel', p: ['#8e3b57', '#4f3b6b', '#c26a3c', '#3d5a49'] },
      { n: 'Quiet whites', p: ['#f6f1e6', '#e8e2d2', '#d9d4c5', '#b9c1a9'] }
    ];
    var HINTS = ['no lilies please', 'more greenery', 'keep it airy', 'wrapped to gift', 'bigger than the photo'];
    var C_OCC = ['Just because', 'Birthday', 'Anniversary', 'New baby', 'Thank you', 'I’m sorry', 'Sympathy', 'Wedding', 'Graduation'];
    var SIZES = ['Petite', 'Classic', 'Grand'];
    var E_OCC = ['Wedding', 'Engagement', 'Graduation', 'Baby reception', 'Birthday', 'Corporate', 'Other'];
    var E_GUE = ['Under 50', '50 to 150', '150 to 300', 'Over 300'];
    var E_NEED = ['Bridal bouquet', 'Stage & backdrop', 'Table centerpieces', 'Welcome stand', 'Entrance arrangements', 'Flower wall', 'Car decoration', 'Giveaways'];

    var layer = null, fscrim = null, rise = null, mode = null, st = null;

    function kNow2() { var n = new Date(); return new Date(n.getTime() + (n.getTimezoneOffset() + 180) * 60000); }
    function isoToday() {
      var n = kNow2(), m = n.getMonth() + 1, d = n.getDate();
      return n.getFullYear() + '-' + (m < 10 ? '0' + m : m) + '-' + (d < 10 ? '0' + d : d);
    }
    function niceDate(iso, fb) {
      if (!iso) return fb;
      var d = new Date(iso + 'T12:00:00');
      return d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
    }
    function esc(s) { return String(s).split('&').join('&amp;').split('<').join('&lt;').split('>').join('&gt;'); }
    function budgetLabel() {
      var cap = mode === 'custom' ? 200 : 3000;
      return 'KD ' + st.budget + (st.budget >= cap ? '+' : '');
    }
    function keptHexes() {
      var out = [];
      for (var i = 0; i < st.swatches.length; i++) if (st.swatches[i].kept) out.push(st.swatches[i].hex);
      return out;
    }
    function blob(m) {
      return 'radial-gradient(circle at 22% 40%, ' + m.p[0] + ' 0 32%, transparent 42%), ' +
        'radial-gradient(circle at 55% 60%, ' + m.p[1] + ' 0 30%, transparent 40%), ' +
        'radial-gradient(circle at 82% 35%, ' + m.p[2] + ' 0 28%, transparent 38%), ' + m.p[3];
    }

    function freshState(m) {
      if (m === 'custom') return { photos: [], swatches: [], mood: '', occ: 'Just because', size: 'Classic', budget: 40, date: '', notes: '' };
      return { photos: [], swatches: [], mood: '', occ: '', date: '', venue: '', guests: '', needs: [], budget: 500, notes: '' };
    }

    function head(title) {
      return '<div class="fluid-ohead">' +
        '<button class="fluid-oback" type="button" aria-label="Close">' +
        '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M15 5 L8 12 L15 19"/></svg>' +
        '</button><h2>' + MARKLET + title + '</h2></div>';
    }
    function foot(label) {
      return '<div class="fluid-ofoot"><p class="fluid-ffoot" data-foot></p>' +
        '<button class="fluid-ogo" type="button" data-send>' + label + '</button></div>';
    }
    function chips(label, items, key, multi, note) {
      var h = '<section class="fluid-ogrp"><h3>' + label + '</h3>' +
        '<div class="fluid-fchipsw" data-k="' + key + '"' + (multi ? ' data-multi="1"' : '') + '>';
      for (var i = 0; i < items.length; i++) {
        h += '<button type="button" class="fluid-fc" data-v="' + items[i] + '">' + items[i] + '</button>';
      }
      h += '</div>' + (note ? '<p class="fluid-onote">' + note + '</p>' : '') + '</section>';
      return h;
    }
    function slider(min, max, step, val, note) {
      return '<section class="fluid-ogrp"><h3>Budget</h3>' +
        '<div class="fluid-fbud"><input type="range" data-f="budget" min="' + min + '" max="' + max + '" step="' + step + '" value="' + val + '" aria-label="Budget in Kuwaiti dinars"><b data-blbl>KD ' + val + '</b></div>' +
        (note ? '<p class="fluid-onote">' + note + '</p>' : '') + '</section>';
    }
    function dateRow(label, note) {
      return '<section class="fluid-ogrp"><h3>' + label + '</h3>' +
        '<input type="date" class="fluid-ofield" data-f="date" aria-label="' + label + '">' +
        (note ? '<p class="fluid-onote">' + note + '</p>' : '') + '</section>';
    }
    function moodBlock(label) {
      var h = '<div class="fluid-moods" data-moods><span class="fluid-flbl">' + label + '</span><div class="fluid-mgrid">';
      for (var i = 0; i < MOODS.length; i++) {
        h += '<button type="button" class="fluid-mood" data-mood="' + MOODS[i].n + '">' +
          '<span class="mblob" style="background:' + blob(MOODS[i]) + '"></span>' +
          '<span class="mname">' + MOODS[i].n + '</span></button>';
      }
      return h + '</div></div>';
    }
    function recapCard(title) {
      return '<section class="fluid-ogrp"><h3>' + title + '</h3><div class="fluid-frecap">' +
        '<div class="fluid-fminis" data-minis hidden></div>' +
        '<div class="fluid-fpal" data-pal hidden><span>Palette</span><span class="fdots" data-dots></span></div>' +
        '<div class="fluid-frows" data-recap></div>' +
        '</div></section>';
    }

    function buildCustom() {
      var h = head('Custom bouquet');
      h += '<div class="fluid-obody">';
      h += '<section class="fluid-ogrp"><h3>Show us</h3>' +
        '<p class="fluid-fhelp">Saw one at a wedding? Screenshotted one at 1am? Add the photo and our florist builds it stem by stem, then writes back with a quote on WhatsApp.</p>' +
        '<div class="fluid-fphotos" data-photos></div>' +
        '<div class="fluid-fswatches" data-swwrap hidden><span class="fluid-flbl">Colors we spotted — tap to keep or drop</span><div class="fluid-fswrow" data-swrow></div></div>' +
        moodBlock('No photo? Pick a mood instead — it sets the palette, the florist takes it from there.') +
        '</section>';
      h += '<section class="fluid-ogrp"><h3>Tell us</h3>' +
        '<textarea class="fluid-ofield" data-f="notes" rows="3" maxlength="400" placeholder="e.g. Like the photo but softer — no lilies please, and heavy on the eucalyptus."></textarea>' +
        '<div class="fluid-fhints">' + hintChips() + '</div></section>';
      h += chips('The occasion', C_OCC, 'occ', false);
      h += chips('How big', SIZES, 'size', false);
      h += slider(20, 200, 5, 40, 'The quote may nudge either way.');
      h += dateRow('Needed by', 'Same day is possible when you order by 9pm.');
      h += recapCard('What our florist sees');
      h += '</div>' + foot('Send to the florist');
      return h;
    }
    function hintChips() {
      var h = '';
      for (var i = 0; i < HINTS.length; i++) {
        h += '<button type="button" class="fluid-fhint" data-h="' + HINTS[i] + '">+ ' + HINTS[i] + '</button>';
      }
      return h;
    }

    function buildEvent() {
      var h = head('Plan your event');
      h += '<div class="fluid-obody">';
      h += '<p class="fluid-fhelp fluid-fhelp-top">Tell us about your day and we write back on WhatsApp with a floral concept and a clear quote. Nothing to pay yet.</p>';
      h += chips('The occasion', E_OCC, 'occ', false);
      h += dateRow('When is it', 'Not sure yet? Leave it empty.');
      h += '<section class="fluid-ogrp"><h3>Where is it</h3>' +
        '<input type="text" class="fluid-ofield" data-f="venue" maxlength="120" placeholder="Hall, home or outdoor — and the area">' +
        '</section>';
      h += chips('How many guests', E_GUE, 'guests', false);
      h += chips('What you need', E_NEED, 'needs', true, 'Pick as many as you like.');
      h += '<section class="fluid-ogrp"><h3>The mood</h3>' + moodBlock('It sets the palette — our stylist takes it from there.') + '</section>';
      h += slider(150, 3000, 50, 500, 'Packages start at KD 350 — the quote follows the design.');
      h += '<section class="fluid-ogrp"><h3>Anything else</h3>' +
        '<textarea class="fluid-ofield" data-f="notes" rows="3" maxlength="400" placeholder="A theme, a flower she loves, a photo of the hall you will send after."></textarea></section>';
      h += recapCard('What our stylist sees');
      h += '</div>' + foot('Send on WhatsApp');
      return h;
    }

    function wire() {
      var el = layer;
      var dt = el.querySelector('[data-f="date"]');
      if (dt) dt.min = isoToday();
      var wraps = el.querySelectorAll('[data-k]');
      for (var i = 0; i < wraps.length; i++) {
        var key = wraps[i].getAttribute('data-k');
        if (wraps[i].hasAttribute('data-multi')) continue;
        var want = st[key];
        if (!want) continue;
        var cs = wraps[i].querySelectorAll('.fluid-fc');
        for (var j = 0; j < cs.length; j++) if (cs[j].getAttribute('data-v') === want) cs[j].classList.add('on');
      }
      renderShow(); renderRecap();
    }

    function renderShow() {
      var el = layer;
      if (!el) return;
      var zone = el.querySelector('[data-photos]');
      if (zone) {
        var h = '';
        for (var i = 0; i < st.photos.length; i++) {
          h += '<span class="fluid-fth" style="background-image:url(' + st.photos[i].src + ')" role="img" aria-label="' + esc(st.photos[i].name) + '">' +
            '<button type="button" class="fluid-fthx" data-i="' + i + '" aria-label="Remove photo">&times;</button></span>';
        }
        if (st.photos.length < MAXPH) {
          h += '<label class="fluid-fadd"><input type="file" accept="image/*" multiple data-file>' +
            '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="3" y="6" width="18" height="14" rx="3"/><circle cx="12" cy="13" r="3.6"/><path d="M9 6l1.2-2h3.6L15 6"/></svg>' +
            '<span>' + (st.photos.length ? st.photos.length + ' of ' + MAXPH : 'Add photos') + '</span></label>';
        }
        zone.innerHTML = h;
      }
      var sww = el.querySelector('[data-swwrap]');
      if (sww) {
        if (st.swatches.length && st.photos.length) {
          sww.hidden = false;
          var swr = el.querySelector('[data-swrow]'), sh = '';
          for (var k = 0; k < st.swatches.length; k++) {
            sh += '<button type="button" class="fluid-fsw' + (st.swatches[k].kept ? ' keep' : '') + '" data-i="' + k + '" style="background:' + st.swatches[k].hex + '" title="' + st.swatches[k].hex + '" aria-label="Colour ' + st.swatches[k].hex + '"></button>';
          }
          swr.innerHTML = sh;
        } else sww.hidden = true;
      }
      var moods = el.querySelector('[data-moods]');
      if (moods) {
        if (mode === 'custom') moods.hidden = st.photos.length > 0;
        var tiles = moods.querySelectorAll('.fluid-mood');
        for (var q = 0; q < tiles.length; q++) {
          tiles[q].classList.toggle('sel', tiles[q].getAttribute('data-mood') === st.mood);
        }
      }
    }

    function row(l, v) { return '<div class="frow"><span>' + l + '</span><span>' + v + '</span></div>'; }

    function renderRecap() {
      var el = layer;
      if (!el) return;
      var box = el.querySelector('[data-recap]');
      var h = '', ok;
      if (mode === 'custom') {
        h += row('Occasion', esc(st.occ));
        h += row('Size', esc(st.size));
        h += row('Budget', 'around ' + budgetLabel());
        h += row('Needed by', niceDate(st.date, 'No rush'));
        if (st.mood) h += row('Mood', esc(st.mood));
        h += row('Notes', st.notes && st.notes.trim() ? esc(st.notes.trim()) : '—');
        ok = st.photos.length > 0 || !!st.mood || !!(st.notes && st.notes.trim());
      } else {
        h += row('Occasion', st.occ ? esc(st.occ) : '—');
        h += row('Date', niceDate(st.date, 'Not set yet'));
        h += row('Venue', st.venue && st.venue.trim() ? esc(st.venue.trim()) : '—');
        h += row('Guests', st.guests ? esc(st.guests) : '—');
        h += row('You need', st.needs.length ? esc(st.needs.join(', ')) : '—');
        if (st.mood) h += row('Mood', esc(st.mood));
        h += row('Budget', 'around ' + budgetLabel());
        h += row('Notes', st.notes && st.notes.trim() ? esc(st.notes.trim()) : '—');
        ok = !!st.occ;
      }
      box.innerHTML = h;
      var pal = el.querySelector('[data-pal]');
      var kept = keptHexes();
      if (pal) {
        pal.hidden = kept.length === 0;
        var dh = '';
        for (var i = 0; i < kept.length; i++) dh += '<span class="fdot" style="background:' + kept[i] + '"></span>';
        el.querySelector('[data-dots]').innerHTML = dh;
      }
      var minis = el.querySelector('[data-minis]');
      if (minis) {
        minis.hidden = st.photos.length === 0;
        var mh = '';
        for (var j = 0; j < st.photos.length; j++) mh += '<span class="fmini" style="background-image:url(' + st.photos[j].src + ')"></span>';
        minis.innerHTML = mh;
      }
      var fnote = el.querySelector('[data-foot]');
      if (fnote) {
        if (mode === 'custom') {
          fnote.textContent = ok
            ? 'A person replies on WhatsApp with a quote — usually same day. Nothing to pay yet.'
            : 'Add a photo, a mood, or a few words first — anything gives us a place to start.';
        } else {
          fnote.textContent = ok
            ? 'We reply on WhatsApp with a concept and a clear quote — nothing to pay yet.'
            : 'Pick the occasion first — everything else is optional.';
        }
      }
      var send = el.querySelector('[data-send]');
      if (send) send.disabled = !ok;
    }

    function extractColors(src) {
      var img = new Image();
      img.onload = function () {
        if (!st) return;
        var size = 64, c = document.createElement('canvas');
        c.width = size; c.height = size;
        var ctx = c.getContext('2d');
        ctx.drawImage(img, 0, 0, size, size);
        var d;
        try { d = ctx.getImageData(0, 0, size, size).data; } catch (err) { return; }
        var buckets = {};
        for (var i = 0; i < d.length; i += 16) {
          var r = d[i], g = d[i + 1], b = d[i + 2];
          var mx = Math.max(r, g, b), mn = Math.min(r, g, b), sat = mx ? (mx - mn) / mx : 0;
          var key = (r >> 5) + '-' + (g >> 5) + '-' + (b >> 5);
          var e = buckets[key] || (buckets[key] = { r: 0, g: 0, b: 0, n: 0, s: 0 });
          e.r += r; e.g += g; e.b += b; e.n++;
          e.s += 0.25 + sat - (mx > 235 && sat < 0.1 ? 0.2 : 0) - (mx < 40 ? 0.2 : 0);
        }
        var cands = [];
        for (var k in buckets) {
          var e2 = buckets[k];
          cands.push({ r: (e2.r / e2.n) | 0, g: (e2.g / e2.n) | 0, b: (e2.b / e2.n) | 0, s: e2.s });
        }
        cands.sort(function (a, b2) { return b2.s - a.s; });
        var picked = [];
        for (var j = 0; j < cands.length && picked.length < 5; j++) {
          var cd = cands[j], far = true;
          for (var q = 0; q < picked.length; q++) {
            var p = picked[q];
            if ((p.r - cd.r) * (p.r - cd.r) + (p.g - cd.g) * (p.g - cd.g) + (p.b - cd.b) * (p.b - cd.b) <= 2600) { far = false; break; }
          }
          if (far) picked.push(cd);
        }
        function hx(v) { var s = v.toString(16); return s.length < 2 ? '0' + s : s; }
        st.swatches = [];
        for (var w = 0; w < picked.length; w++) {
          st.swatches.push({ hex: '#' + hx(picked[w].r) + hx(picked[w].g) + hx(picked[w].b), kept: true });
        }
        renderShow(); renderRecap();
      };
      img.src = src;
    }

    function onTap(e) {
      if (e.target.closest('.fluid-oback')) { close(); return; }
      var chip = e.target.closest('.fluid-fc');
      if (chip) {
        var wrap = chip.parentNode, key = wrap.getAttribute('data-k');
        buzz(6);
        if (wrap.hasAttribute('data-multi')) {
          chip.classList.toggle('on');
          var sel = [], all = wrap.querySelectorAll('.fluid-fc.on');
          for (var i = 0; i < all.length; i++) sel.push(all[i].getAttribute('data-v'));
          st[key] = sel;
        } else {
          var on = wrap.querySelectorAll('.fluid-fc.on');
          for (var j = 0; j < on.length; j++) if (on[j] !== chip) on[j].classList.remove('on');
          chip.classList.add('on');
          st[key] = chip.getAttribute('data-v');
        }
        renderRecap(); return;
      }
      var mood = e.target.closest('.fluid-mood');
      if (mood) {
        buzz(6);
        var name = mood.getAttribute('data-mood');
        st.mood = st.mood === name ? '' : name;
        st.swatches = [];
        if (st.mood) {
          for (var k = 0; k < MOODS.length; k++) {
            if (MOODS[k].n !== st.mood) continue;
            for (var q = 0; q < MOODS[k].p.length; q++) st.swatches.push({ hex: MOODS[k].p[q], kept: true });
          }
        }
        renderShow(); renderRecap(); return;
      }
      var sw = e.target.closest('.fluid-fsw');
      if (sw) {
        buzz(5);
        var ix = parseInt(sw.getAttribute('data-i'), 10);
        if (st.swatches[ix]) st.swatches[ix].kept = !st.swatches[ix].kept;
        renderShow(); renderRecap(); return;
      }
      var hint = e.target.closest('.fluid-fhint');
      if (hint) {
        buzz(5);
        var ta = layer.querySelector('[data-f="notes"]');
        if (ta) {
          var cur = ta.value.trim();
          while (cur.length && (cur.charAt(cur.length - 1) === ',' || cur.charAt(cur.length - 1) === ' ')) cur = cur.slice(0, -1);
          ta.value = (cur ? cur + ', ' : '') + hint.getAttribute('data-h');
          st.notes = ta.value;
        }
        renderRecap(); return;
      }
      var rm = e.target.closest('.fluid-fthx');
      if (rm) {
        var pi = parseInt(rm.getAttribute('data-i'), 10);
        st.photos.splice(pi, 1);
        if (!st.photos.length) st.swatches = [];
        renderShow(); renderRecap();
        if (st.photos.length && pi === 0) extractColors(st.photos[0].src);
        return;
      }
      if (e.target.closest('[data-send]')) { send(); return; }
      if (e.target.closest('[data-again]')) {
        st = freshState(mode);
        layer.innerHTML = mode === 'custom' ? buildCustom() : buildEvent();
        wire(); return;
      }
      if (e.target.closest('[data-done]')) close();
    }

    function onInput(e) {
      var f = e.target.getAttribute && e.target.getAttribute('data-f');
      if (!f) return;
      if (f === 'budget') {
        st.budget = parseInt(e.target.value, 10);
        var bl = layer.querySelector('[data-blbl]');
        if (bl) bl.textContent = budgetLabel();
        renderRecap(); return;
      }
      st[f] = e.target.value;
      renderRecap();
    }

    function onChange(e) {
      if (e.target.hasAttribute && e.target.hasAttribute('data-file')) {
        var files = e.target.files, take = [];
        var room = MAXPH - st.photos.length;
        for (var i = 0; i < files.length && take.length < room; i++) {
          if (files[i].type && files[i].type.indexOf('image/') === 0) take.push(files[i]);
        }
        for (var j = 0; j < take.length; j++) {
          (function (f) {
            var rd = new FileReader();
            rd.onload = function () {
              if (!st || mode !== 'custom') return;
              if (st.photos.length >= MAXPH) return;
              st.photos.push({ src: rd.result, name: f.name });
              st.mood = '';
              if (st.photos.length === 1) extractColors(rd.result);
              renderShow(); renderRecap();
            };
            rd.readAsDataURL(f);
          })(take[j]);
        }
        e.target.value = '';
        return;
      }
      var f2 = e.target.getAttribute && e.target.getAttribute('data-f');
      if (f2 === 'date') { st.date = e.target.value; renderRecap(); }
    }

    function send() {
      var NL = String.fromCharCode(10);
      var lines = [];
      if (mode === 'custom') {
        lines.push('Hello October Twenty! Custom bouquet request:');
        if (st.mood) lines.push('Mood: ' + st.mood);
        lines.push('Occasion: ' + st.occ);
        lines.push('Size: ' + st.size);
        lines.push('Budget: around ' + budgetLabel());
        lines.push('Needed by: ' + niceDate(st.date, 'No rush'));
        var kept = keptHexes();
        if (kept.length) lines.push('Palette: ' + kept.join(', '));
        if (st.notes && st.notes.trim()) lines.push('Notes: ' + st.notes.trim());
        if (st.photos.length) lines.push('I have ' + st.photos.length + ' reference photo' + (st.photos.length > 1 ? 's' : '') + ' — attaching here.');
      } else {
        lines.push('Hello October Twenty! Event styling request:');
        lines.push('Occasion: ' + st.occ);
        if (st.date) lines.push('Date: ' + niceDate(st.date, ''));
        if (st.venue && st.venue.trim()) lines.push('Venue: ' + st.venue.trim());
        if (st.guests) lines.push('Guests: ' + st.guests);
        if (st.needs.length) lines.push('We need: ' + st.needs.join(', '));
        if (st.mood) lines.push('Mood: ' + st.mood);
        lines.push('Budget: around ' + budgetLabel());
        if (st.notes && st.notes.trim()) lines.push('Notes: ' + st.notes.trim());
      }
      buzz(10);
      window.open('https://wa.me/' + WA + '?text=' + encodeURIComponent(lines.join(NL)), '_blank', 'noopener');
      showDone();
    }

    function showDone() {
      var custom = mode === 'custom';
      var t = custom ? 'Off to the bench it goes' : 'Off to the planning desk';
      var body = custom
        ? (st.photos.length
          ? 'WhatsApp is open with your request — attach your photo' + (st.photos.length > 1 ? 's' : '') + ' there and press send. A real florist reads every request and writes back with a quote, usually before the day is out. Nothing to pay until you say yes.'
          : 'WhatsApp is open with your request — press send there. A real florist reads every request and writes back with a quote, usually before the day is out. Nothing to pay until you say yes.')
        : 'WhatsApp is open with your request — press send there. We write back with a floral concept and a clear quote for your day. Nothing to pay until you say yes.';
      layer.innerHTML = head(custom ? 'Custom bouquet' : 'Plan your event') +
        '<div class="fluid-obody"><div class="fluid-fdone">' +
        '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4 12.5l5 5L20 6.5"/></svg>' +
        '<h3>' + t + '</h3><p>' + body + '</p>' +
        '<div class="fluid-fdbtns"><button type="button" class="fluid-ogo" data-done>Done</button>' +
        '<button type="button" class="fluid-fagain" data-again>Start another</button></div>' +
        '</div></div>';
    }

    function kbWatch(el) {
      var vv = window.visualViewport;
      if (!vv) return;
      var tick = false;
      function place() {
        tick = false;
        if (!el.isConnected) return;
        if (!kbFocused()) { el.style.bottom = ''; return; }
        var off = Math.max(0, innerHeight - vv.height - vv.offsetTop);
        el.style.bottom = off > 1 ? off + 'px' : '';
      }
      function ping() { if (tick) return; tick = true; requestAnimationFrame(place); }
      vv.addEventListener('resize', ping);
      vv.addEventListener('scroll', ping);
      el._kbOff = function () { vv.removeEventListener('resize', ping); vv.removeEventListener('scroll', ping); };
    }

    function open(m) {
      if (layer) return;
      mode = m; st = freshState(m);
      buzz(8);
      fscrim = document.createElement('div');
      fscrim.className = 'fluid-oscrim';
      document.body.appendChild(fscrim);
      var el = document.createElement('div');
      el.className = 'fluid-ord fluid-flow';
      el.setAttribute('role', 'dialog');
      el.setAttribute('aria-label', m === 'custom' ? 'Custom bouquet' : 'Plan your event');
      el.innerHTML = m === 'custom' ? buildCustom() : buildEvent();
      document.body.appendChild(el);
      layer = el;
      lockScroll();
      if (m === 'custom') document.dispatchEvent(new CustomEvent('fluidbag', { detail: { open: true, name: 'custom' } }));
      /* capture locals: close nulls the module vars while the spring is
         still settling (the pass-8 lesson, and again in pass 12) */
      var sc = fscrim;
      rise = new Spring(102, function (y) {
        if (!el.isConnected) return;
        el.style.transform = 'translate3d(0,' + y + '%,0)';
        sc.style.opacity = String(Math.max(0, 1 - y / 102));
      });
      rise.set(0, { damping: 1, response: 0.58 });
      el.addEventListener('click', onTap);
      el.addEventListener('input', onInput);
      el.addEventListener('change', onChange);
      kbWatch(el);
      custClose = close;
      wire();
    }

    function close() {
      if (!layer) return;
      var el = layer, sc = fscrim, m = mode;
      if (el._kbOff) el._kbOff();
      el.style.bottom = '';
      layer = null; fscrim = null; custClose = null; mode = null; st = null;
      buzz(8);
      if (m === 'custom') document.dispatchEvent(new CustomEvent('fluidbag', { detail: { open: false } }));
      rise.set(102, {
        damping: 1, response: 0.44, onRest: function () {
          el.remove(); if (sc) sc.remove(); unlockScroll();
        }
      });
    }

    window.fluidOpenCustom = function () { open('custom'); };
    window.fluidOpenEvent = function () { open('event'); };
    document.addEventListener('click', function (e) {
      var b = e.target.closest('[data-opencustom]');
      if (b) { e.preventDefault(); open('custom'); return; }
      var v = e.target.closest('[data-openevent]');
      if (v) { e.preventDefault(); open('event'); }
    });
  })();

  /* ---------------- pages rise as sheets: the ord grammar everywhere -----
     CEO, 21 Aug: pages should show the way the order details page shows, so
     the home never navigates and never reloads. Any /pages/ or /policies/
     link opens a full-screen layer; the content is fetched once and kept.
     Products and collections stay real pages, as he asked. */
  (function () {
    var layer = null, rise = null, cache = {};
    function head(title) {
      return '<div class="fluid-ohead">' +
        '<button class="fluid-oback" type="button" aria-label="Close">' +
        '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M15 5 L8 12 L15 19"/></svg>' +
        '</button><h2>' + MARKLET + '<span data-pgtitle>' + title + '</span></h2></div>' +
        '<div class="fluid-obody" data-pgbody><div class="fluid-pgwait">' + MARKLET + '</div></div>';
    }
    function inject(el, html, path) {
      var body = el.querySelector('[data-pgbody]');
      if (!body) return;
      var doc;
      try { doc = new DOMParser().parseFromString(html, 'text/html'); } catch (err) { location.href = path; return; }
      var frag = doc.querySelector('.fluid-page-wrap') || doc.querySelector('.shopify-policy__container');
      if (!frag) { location.href = path; return; }
      var junk = frag.querySelectorAll('.fluid-promise, .fluid-info');
      for (var i = 0; i < junk.length; i++) junk[i].parentNode.removeChild(junk[i]);
      var shell = document.createElement('div');
      shell.className = 'fluid-page fluid-inlayer';
      var wrap = document.createElement('div');
      wrap.className = 'fluid-page-wrap';
      if (frag.className.indexOf('shopify-policy__container') > -1) wrap.className += ' shopify-policy__container';
      while (frag.firstChild) wrap.appendChild(frag.firstChild);
      shell.appendChild(wrap);
      body.innerHTML = '';
      body.appendChild(shell);
      var revs = body.querySelectorAll('.fluid-reveal');
      for (var r = 0; r < revs.length; r++) (function (n, k) {
        setTimeout(function () { n.classList.add('in'); }, 40 + k * 45);
      })(revs[r], r);
      body.scrollTop = 0;
    }
    function load(el, path) {
      if (cache[path]) { inject(el, cache[path], path); return; }
      fetch(path).then(function (r) {
        if (!r.ok) throw new Error('page fetch failed');
        return r.text();
      }).then(function (t) {
        cache[path] = t;
        if (layer === el) inject(el, t, path);
      }).catch(function () { location.href = path; });
    }
    function open(path, title) {
      if (layer) {
        var tEl = layer.querySelector('[data-pgtitle]');
        if (tEl) tEl.textContent = title;
        var body = layer.querySelector('[data-pgbody]');
        if (body) body.innerHTML = '<div class="fluid-pgwait">' + MARKLET + '</div>';
        load(layer, path);
        return;
      }
      buzz(8);
      var sc = document.createElement('div');
      sc.className = 'fluid-oscrim';
      document.body.appendChild(sc);
      var el = document.createElement('div');
      el.className = 'fluid-ord fluid-pgl';
      el.setAttribute('role', 'dialog');
      el.setAttribute('aria-label', title);
      el.innerHTML = head(title);
      document.body.appendChild(el);
      layer = el;
      el._sc = sc;
      lockScroll();
      rise = new Spring(102, function (y) {
        if (!el.isConnected) return;
        el.style.transform = 'translate3d(0,' + y + '%,0)';
        sc.style.opacity = String(Math.max(0, 1 - y / 102));
      });
      rise.set(0, { damping: 1, response: 0.58 });
      el.addEventListener('click', function (e) {
        if (e.target.closest('.fluid-oback')) closePg();
      });
      pgClose = closePg;
      load(el, path);
    }
    function closePg() {
      if (!layer) return;
      var el = layer, sc = el._sc;
      layer = null; pgClose = null;
      buzz(8);
      rise.set(102, {
        damping: 1, response: 0.44, onRest: function () {
          el.remove(); if (sc) sc.remove(); unlockScroll();
        }
      });
    }
    document.addEventListener('click', function (e) {
      var a = e.target.closest('a[href]');
      if (!a || e.defaultPrevented || e.metaKey || e.ctrlKey || e.shiftKey || a.target === '_blank') return;
      var href = a.getAttribute('href');
      if (!href || href.charAt(0) !== '/') return;
      var path = href.split('#')[0].split('?')[0];
      if (path.indexOf('/pages/') !== 0 && path.indexOf('/policies/') !== 0) return;
      e.preventDefault();
      open(path, (a.textContent || '').trim() || 'October Twenty');
    });
  })();

  /* ---------------- gift cards: pick an amount, into the bag ------------- */
  (function () {
    document.addEventListener('click', function (e) {
      var amt = e.target.closest('.fluid-gamt');
      if (amt && !amt.disabled) {
        var wrap = amt.closest('.fluid-gamts');
        if (wrap) {
          var all = wrap.querySelectorAll('.fluid-gamt');
          for (var i = 0; i < all.length; i++) all[i].classList.remove('on');
        }
        amt.classList.add('on');
        buzz(6);
        return;
      }
      var go = e.target.closest('[data-giftadd]');
      if (!go || go.disabled) return;
      var box = go.closest('[data-gift]');
      var on = box ? box.querySelector('.fluid-gamt.on') : null;
      if (!on) return;
      go.disabled = true;
      var old = go.textContent;
      go.textContent = TT('wait', 'One moment');
      fetch('/cart/add.js', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: parseInt(on.getAttribute('data-vid'), 10), quantity: 1 }] })
      }).then(function (r) {
        if (!r.ok) throw new Error('add failed');
        return refreshCart();
      }).then(function () {
        buzz(10);
        go.disabled = false;
        go.textContent = old;
        if (pgClose) pgClose();
        if (window.fluidOpenBag) window.fluidOpenBag();
      }).catch(function () { go.disabled = false; go.textContent = old; });
    });
  })();

  /* ---------------- the cash gift, rebuilt on the gifts page -------------
     CEO, 23 Aug: "add the cash gift option there, the one we worked on
     before, and show it nicely according to the facelift theme."

     Same engine as the 2026-08-06 build, same two hidden utility products.
     The fee table is the one in Business Facts, NOT the older note in the
     session log: KNET and Visa are both 10% (the 3-per-50 KNET rate was
     retired 2026-08-06 because checkout cannot split KNET from Visa inside
     the Hesabe button, so a cheaper KNET rate was gameable), Tabby is 25%
     and only offered when the bouquet is 20 KD or more.

     Free delivery counts FLOWERS only. When the flowers are under 45 but the
     cash pushes the cart over Shopify's threshold, Shopify would hand out
     free shipping it should not - so the 3 KD is folded into the fee line
     instead. Either way the customer is charged the total shown, which is
     the only number this page displays (CEO: total only, no breakdown). */
  (function () {
    /* Bound to the DOCUMENT, not to the block.

       23 Aug, CEO, twice: "the buttons are not working... i can't choose the
       amount or payment method." The handler used to be attached to the
       [data-cg] element itself, resolved once at load. This block lives inside
       a Shopify SECTION, and a section gets re-rendered - by the theme editor
       on load and after every edit, and by any section refresh. When that
       happens the original element is thrown away and the listener goes with
       it, leaving every control in the block inert: no amount, no rail,
       nothing. Testing a plain preview URL never re-renders, which is why a
       green suite sat alongside a page he could not use.

       Delegating from the document survives every re-render, and boot() runs
       again on shopify:section:load so the state starts clean. */
    /* 24 Aug (CEO): ONE flat rate on every payment type.
       28 Aug (CEO): the gift is STANDALONE - the bouquet left this page.
       The customer sets the amount here and picks any bouquet from the shop
       afterwards; the cart-level rules (no checkout without a bouquet, the
       delivery fold) live at the top of this file with giftFeeSettle. The
       rate is CG_FEE_PCT, declared once up there beside them. */
    var state = { amount: 0 };

    function feeMinor() { return Math.round(state.amount * 100 * CG_FEE_PCT / 100); }
    function totalMinor() { return state.amount * 100 + feeMinor(); }
    /* 10 KD floor as before; 200 KD cap from the 27 Aug sweep - with no
       upper bound a typo like 1000 silently priced a 1,000 KD gift plus a
       200 KD fee. 200 is the max the ORIGINAL builder shipped with; the CEO
       can raise it, but a typo can no longer set it. */
    var MAX_GIFT_KD = 200;
    function readyToPrice() { return state.amount >= 10 && state.amount <= MAX_GIFT_KD; }
    function setLine(box, key, minor) {
      var el = box.querySelector('[data-cgline="' + key + '"]');
      if (el) { el.textContent = KD(minor); }
    }
    function paint(box) {
      var totalEl = box.querySelector('[data-cgtotal]');
      var goEl = box.querySelector('[data-cggo]');
      var calcEl = box.querySelector('[data-cgcalc]');
      var agree = box.querySelector('[data-cgagree]');
      var ready = readyToPrice();

      if (calcEl) { calcEl.hidden = !ready; }
      if (ready) {
        setLine(box, 'gift', state.amount * 100);
        setLine(box, 'fee', feeMinor());
      }
      if (totalEl) { totalEl.textContent = ready ? KD(totalMinor()) : '-'; }
      /* Continue needs BOTH a priced gift and the agreement ticked. The
         checkbox is read from the DOM rather than kept in state, so a section
         re-render cannot leave the button enabled against an unticked box. */
      /* 31 Aug 2026: WhatsApp-request mode - the terms tick is out of the
         markup (no cash-gift terms are published to agree to yet, and nothing
         is paid on this page), so the button waits on a priced gift only.
         This form still honours the tick if the label is ever restored. */
      if (goEl) { goEl.disabled = !(ready && (!agree || agree.checked)); }
    }

    document.addEventListener('click', function (ev) {
      var box = ev.target.closest('[data-cg]'); if (!box) { return; }

      var chip = ev.target.closest('[data-cgamt]');
      if (chip) {
        state.amount = parseInt(chip.getAttribute('data-cgamt'), 10);
        var chips = box.querySelectorAll('[data-cgamt]');
        for (var i = 0; i < chips.length; i++) { chips[i].classList.remove('on'); }
        chip.classList.add('on');
        var inp = box.querySelector('[data-cgin]'); if (inp) { inp.value = state.amount; }
        buzz(6); paint(box); return;
      }

      var go = ev.target.closest('[data-cggo]');
      if (!go || go.disabled) { return; }
      /* 31 Aug 2026 (CEO): "make the cash gift option ends up with a request
         sent to us on whatsapp until we confirm with the lawyer our terms and
         conditions for protection." Nothing touches the cart any more: the
         customer sends a WhatsApp request and the shop confirms and prices it
         by hand, exactly like the custom-bouquet and event flows. The cart
         path this replaced (add data-giftvid + data-feevid via /cart/add.js,
         then /collections/our-bouquets) is in the repo history and comes back
         the day legal clears the terms; giftSplit / giftFeeSettle / the
         checkout gate upstream stay, dormant, for that day. The message says
         ESTIMATE on purpose - the final total is confirmed in the chat before
         any payment, which is what the note under the button promises. */
      var NL = String.fromCharCode(10);
      var msg = [
        'Hello October Twenty! Cash gift request:',
        'Gift amount: ' + KD(state.amount * 100),
        'Estimated total with the service fee: ' + KD(totalMinor()),
        'Please confirm my request and the final total with me before I pay anything.'
      ].join(NL);
      buzz(10);
      window.open('https://wa.me/96599548711?text=' + encodeURIComponent(msg), '_blank', 'noopener');
      var sent = box.querySelector('[data-cgsent]');
      if (sent) { sent.hidden = false; }
    });

    document.addEventListener('input', function (ev) {
      var inp = ev.target.closest('[data-cgin]'); if (!inp) { return; }
      var box = inp.closest('[data-cg]'); if (!box) { return; }
      var v = parseInt(inp.value, 10);
      state.amount = isNaN(v) ? 0 : v;
      var chips = box.querySelectorAll('[data-cgamt]');
      for (var i = 0; i < chips.length; i++) {
        chips[i].classList.toggle('on', parseInt(chips[i].getAttribute('data-cgamt'), 10) === state.amount);
      }
      paint(box);
    });

    document.addEventListener('change', function (ev) {
      if (!ev.target || !ev.target.closest) { return; }
      if (!ev.target.closest('[data-cgagree]')) { return; }
      var box = ev.target.closest('[data-cg]'); if (!box) { return; }
      paint(box);
    });

    function boot() {
      var box = document.querySelector('[data-cg]');
      if (!box) { return; }
      state.amount = 0;
      var agree = box.querySelector('[data-cgagree]');
      if (agree) { agree.checked = false; }
      paint(box);
    }
    boot();
    document.addEventListener('shopify:section:load', boot);
  })();

  /* ---------------- the loading mark: the lotus turns while we work ------ */
  var loadEl = null, loadTimer = 0;
  function fluidLoad(on) {
    if (on) {
      if (!loadEl) {
        loadEl = document.createElement('div');
        loadEl.className = 'fluid-loadmark';
        loadEl.innerHTML = '<span class="lmspin">' + MARKLET + '</span>';
        document.body.appendChild(loadEl);
      }
      clearTimeout(loadTimer);
      loadTimer = setTimeout(function () { if (loadEl) loadEl.classList.add('in'); }, 160);
    } else {
      clearTimeout(loadTimer);
      if (loadEl) loadEl.classList.remove('in');
    }
  }
  window.fluidLoad = fluidLoad;
  addEventListener('pageshow', function () { fluidLoad(false); });
  document.addEventListener('click', function (e) {
    var a = e.target.closest('a[href]');
    if (!a || e.defaultPrevented || e.metaKey || e.ctrlKey || e.shiftKey || a.target === '_blank') return;
    var href = a.getAttribute('href') || '';
    if (href.charAt(0) !== '/' || href.charAt(1) === '/') return;
    /* /pages/ and /policies/ are NOT navigations here - the page-layer
       module intercepts them and opens the in-page sheet, which draws its
       own fluid-pgwait mark while it fetches. Round 31 nearly deleted
       this exclusion thinking it was the loading-screen bug; it is load-
       bearing. The real gap was the INITIAL load, covered by the splash
       in fluid-bar. */
    if (href.indexOf('/pages/') === 0 || href.indexOf('/policies/') === 0) return;
    fluidLoad(true);
  });

  /* ---------------- pull to refresh: the lotus blooms, the page renews ---
     CEO, 21 Aug. Touch only, engages only from the very top of the page,
     and never while a sheet is up. */
  (function () {
    if (REDUCE.matches) return;
    var el = null, sp = null, active = null, engaged = false;
    function make() {
      if (el) return;
      el = document.createElement('div');
      el.className = 'fluid-ptr';
      el.innerHTML = MARKLET;
      document.body.appendChild(el);
      sp = new Spring(0, function (t) {
        if (!el) return;
        var q = Math.max(0, t);
        el.style.transform = 'translate3d(-50%,' + (q * 86 - 64) + 'px,0) rotate(' + (q * 300) + 'deg)';
        el.style.opacity = String(Math.min(1, q * 1.6));
      });
    }
    document.addEventListener('touchstart', function (e) {
      if (locks > 0 || scrollY > 0 || e.touches.length !== 1) { active = null; return; }
      active = { y: e.touches[0].clientY, x: e.touches[0].clientX }; engaged = false;
    }, { passive: true });
    document.addEventListener('touchmove', function (e) {
      if (!active) return;
      var dy = e.touches[0].clientY - active.y;
      var dx = e.touches[0].clientX - active.x;
      if (!engaged) {
        if (dy < 0 || Math.abs(dx) > Math.abs(dy)) { active = null; return; }
        if (dy < 8) return;
        if (scrollY > 0) { active = null; return; }
        engaged = true; make(); sp.stop();
      }
      if (e.cancelable) e.preventDefault();
      sp.jump(Math.min(1.35, rubber(dy, 600) / 86));
    }, { passive: false });
    function end() {
      if (!active) return;
      var was = engaged; active = null; engaged = false;
      if (!was || !sp) return;
      if (sp.x >= 0.85) {
        buzz(12);
        if (el) el.classList.add('go');
        fluidLoad(true);
        sp.set(0.75, { damping: 1, response: 0.4 });
        setTimeout(function () {
          if (window.fluidReload) window.fluidReload(); else location.reload();
        }, 380);
      } else {
        sp.set(0, { damping: damp(), response: 0.4, onRest: function () {
          if (el) { el.remove(); el = null; sp = null; }
        } });
      }
    }
    document.addEventListener('touchend', end, { passive: true });
    document.addEventListener('touchcancel', end, { passive: true });
  })();

  /* ---------------- collection filter: the capsule from the home ----------
     CEO, 20 Aug: the filter on the all-bouquets page gets the same motion
     as the chips he loved on the home - the gold capsule springs between
     the active filters while the grid swaps in place. */
  (function () {
    var coll2 = document.querySelector('.fluid-coll');
    if (!coll2) return;
    var chips2 = coll2.querySelector('.fluid-fchips');
    var cap2 = chips2 ? chips2.querySelector('.fluid-capsule') : null;
    if (!chips2 || !cap2) return;
    var fx = new Spring(0, function (x) { cap2.style.transform = 'translate3d(' + x + 'px,0,0)'; });
    var fw = new Spring(0, function (w) { cap2.style.width = w + 'px'; });
    function placeF(chip, instant) {
      var x = chip.offsetLeft, w = chip.offsetWidth;
      if (instant || REDUCE.matches) { fx.jump(x); fw.jump(w); return; }
      fx.set(x, { damping: damp(), response: 0.35 });
      fw.set(w, { damping: 1, response: 0.35 });
    }
    chips2.addEventListener('click', function (e) {
      var a = e.target.closest('.fluid-fchip'); if (!a) return;
      placeF(a);
    });
    requestAnimationFrame(function () {
      var on = chips2.querySelector('.fluid-fchip.on') || chips2.querySelector('.fluid-fchip');
      if (on) { on.classList.add('on'); placeF(on, true); }
    });
    addEventListener('resize', function () {
      var on = chips2.querySelector('.fluid-fchip.on'); if (on) placeF(on, true);
    });
  })();

  /* ---------------- browse: the collection page --------------------------
     Filtering and Load more both re-render THIS section on the server and
     swap the grid in place, so the chrome never blinks and the catalogue can
     be as long as it likes. -------------------------------------------- */
  (function () {
    var coll = document.querySelector('.fluid-coll');
    if (!coll) return;
    var grid = coll.querySelector('.fluid-grid');
    var chips = coll.querySelector('.fluid-fchips');
    var moreWrap = coll.querySelector('.fluid-more-wrap');
    var sid = coll.getAttribute('data-sid');
    var busy = false;

    /* a different order each visit, stable within the visit (CEO, 21 Aug):
       the shelf reshuffles for a new session, not under their thumb */
    var cseed = 0;
    try { cseed = parseInt(sessionStorage.getItem('flmixc') || '0', 10); } catch (err) {}
    if (!cseed) {
      cseed = (Date.now() % 899981) + 17;
      try { sessionStorage.setItem('flmixc', String(cseed)); } catch (err) {}
    }
    function shuffleGrid() {
      var kids = grid.querySelectorAll('.fluid-gwrap');
      if (kids.length < 3) return;
      var cs = cseed, arr = [], i, j, t;
      for (i = 0; i < kids.length; i++) arr.push(kids[i]);
      function rnd() { cs = (cs * 48271) % 2147483647; return cs / 2147483647; }
      for (i = arr.length - 1; i > 0; i--) {
        j = Math.floor(rnd() * (i + 1));
        t = arr[i]; arr[i] = arr[j]; arr[j] = t;
      }
      var frag = document.createDocumentFragment();
      for (i = 0; i < arr.length; i++) frag.appendChild(arr[i]);
      grid.appendChild(frag);
    }
    shuffleGrid();

    function pull(url) {
      var u = url + (url.indexOf('?') > -1 ? '&' : '?') + 'section_id=' + encodeURIComponent(sid);
      return fetch(u).then(function (r) { return r.text(); }).then(function (t) {
        var box = document.createElement('div');
        box.innerHTML = t;
        return box;
      });
    }
    function swapMore(box) {
      var nb = box.querySelector('.fluid-more');
      var cur = moreWrap.querySelector('.fluid-more');
      if (nb) {
        if (cur) { cur.setAttribute('data-next', nb.getAttribute('data-next')); cur.disabled = false; cur.textContent = 'Load more'; }
        else moreWrap.innerHTML = nb.outerHTML;
        moreWrap.hidden = false;
      } else { moreWrap.innerHTML = ''; moreWrap.hidden = true; }
    }

    if (chips) chips.addEventListener('click', function (e) {
      var a = e.target.closest('.fluid-fchip');
      if (!a || busy) return;
      e.preventDefault();
      busy = true; buzz(6);
      var all = chips.querySelectorAll('.fluid-fchip');
      for (var i = 0; i < all.length; i++) all[i].classList.remove('on');
      a.classList.add('on');
      var href = a.getAttribute('href');
      grid.classList.add('swapping');
      pull(href).then(function (box) {
        var ng = box.querySelector('.fluid-grid');
        grid.innerHTML = ng ? ng.innerHTML : '';
        shuffleGrid();
        swapMore(box);
        grid.classList.remove('swapping');
        try { history.replaceState({}, '', href); } catch (err) {}
        busy = false;
      }).catch(function () { grid.classList.remove('swapping'); busy = false; location.href = href; });
    });

    if (moreWrap) moreWrap.addEventListener('click', function (e) {
      var b = e.target.closest('.fluid-more');
      if (!b || busy) return;
      busy = true; b.disabled = true; b.textContent = 'Loading';
      pull(b.getAttribute('data-next')).then(function (box) {
        var ng = box.querySelector('.fluid-grid');
        if (ng) {
          var kids = ng.children, frag = document.createDocumentFragment();
          while (kids.length) frag.appendChild(kids[0]);
          grid.appendChild(frag);
        }
        swapMore(box);
        busy = false;
      }).catch(function () { b.disabled = false; b.textContent = 'Load more'; busy = false; });
    });
  })();

  /* ---------------- the product page ------------------------------------
     A shared link lands here rather than on a card, so it gets the same
     gallery and the same flight into the bag. --------------------------- */
  (function () {
    var pdp = document.querySelector('.fluid-pdp');
    if (!pdp) return;
    var qty = 1;
    var qv = pdp.querySelector('.qv');
    var shot = pdp.querySelector('.fluid-pdp-shot');
    var imgs = null;
    var addBtn = pdp.querySelector('.fluid-pdp-add');

    /* toppers (CEO, 21 Aug): tick one and it rides into the bag with the
       bouquet, and the button carries the running total */
    function topsOn() { return pdp.querySelectorAll('[data-topper]:checked'); }
    function updAdd() {
      if (!addBtn || addBtn.disabled) return;
      var raw = parseInt(pdp.getAttribute('data-vraw') || '0', 10) * qty;
      var tops = topsOn(), i;
      for (i = 0; i < tops.length; i++) raw += parseInt(tops[i].getAttribute('data-tprice') || '0', 10);
      if (tops.length && raw > 0) addBtn.textContent = 'Add to Bag ' + String.fromCharCode(183) + ' ' + (raw / 100).toFixed(3) + KD_UNIT;
      else if (addBtn.textContent.indexOf(String.fromCharCode(183)) > -1) addBtn.textContent = 'Add to Bag';
    }
    pdp.addEventListener('change', function (e) {
      if (e.target && e.target.hasAttribute && e.target.hasAttribute('data-topper')) { buzz(6); updAdd(); }
    });

    fetch('/products/' + pdp.getAttribute('data-handle') + '.js')
      .then(function (r) { return r.json(); })
      .then(function (prod) {
        if (prod.images && prod.images.length) imgs = prod.images.map(function (u) {
          return u + (u.indexOf('?') > -1 ? '&' : '?') + 'width=1800';
        });
      }).catch(function () {});

    pdp.addEventListener('click', function (e) {
      var st = e.target.closest('.fluid-stepper button');
      if (st) {
        qty = Math.max(1, Math.min(20, qty + parseInt(st.getAttribute('data-s'), 10)));
        if (qv) qv.textContent = String(qty);
        updAdd();
        buzz(5);
        return;
      }
      var vb = e.target.closest('.fluid-var');
      if (vb && !vb.disabled) {
        var vs = pdp.querySelectorAll('.fluid-var');
        for (var i = 0; i < vs.length; i++) vs[i].classList.remove('on');
        vb.classList.add('on');
        pdp.setAttribute('data-variant', vb.getAttribute('data-vid'));
        var pr = pdp.querySelector('[data-price]');
        if (pr) pr.textContent = vb.getAttribute('data-vprice');
        pdp.setAttribute('data-vraw', vb.getAttribute('data-vraw') || '0');
        updAdd();
        if (window.fluidTabby) window.fluidTabby(parseInt(vb.getAttribute('data-vraw') || '0', 10) / 100);
        buzz(6);
        return;
      }
      var gal = e.target.closest('.fluid-pdp-gal');
      if (gal) { if (imgs && imgs.length) openGallery(imgs); return; }
      var add = e.target.closest('.fluid-pdp-add');
      if (add && !add.disabled) addNow(add);
    });

    function addNow(btn) {
      btn.disabled = true;
      fetch('/cart/add.js', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: (function () {
          var list = [{ id: parseInt(pdp.getAttribute('data-variant'), 10), quantity: qty }];
          var tops = topsOn(), i;
          for (i = 0; i < tops.length; i++) list.push({ id: parseInt(tops[i].getAttribute('data-tid'), 10), quantity: 1 });
          return list;
        })() })
      }).then(function (r) {
        if (!r.ok) throw new Error('add failed');
        return refreshCart();
      }).then(function () {
        buzz(10);
        fly(btn, shot ? shot.src : '', null);
        btn.disabled = false;
        var tops = topsOn(), i;
        for (i = 0; i < tops.length; i++) tops[i].checked = false;
        btn.textContent = 'Added';
        setTimeout(function () { btn.textContent = 'Add to Bag'; updAdd(); }, 1400);
      }).catch(function () { btn.disabled = false; });
    }
  })();

  /* ---------------- the cart page ---------------------------------------
     The bag sheet is the normal home for this, but /cart has to work for
     anyone who lands on it. Same lines, same totals. ------------------- */
  (function () {
    var cp = document.querySelector('.fluid-cart-page');
    if (!cp) return;
    var lines = cp.querySelector('[data-cartlines]');
    var totals = cp.querySelector('[data-carttotals]');

    function paint() {
      if (!CART) return;
      if (!CART.items.length) {
        lines.innerHTML = '<div class="fluid-cempty">' + TT('empty', 'Nothing in the bag yet.') + '</div>';
        totals.innerHTML = '';
        var go = cp.querySelector('[data-openorder]');
        if (go) go.style.display = 'none';
        return;
      }
      var html = '';
      for (var i = 0; i < CART.items.length; i++) html += lineHTML(CART.items[i]);
      lines.innerHTML = html;
      if (giftSplit(CART).gift) {
        totals.innerHTML = giftTotalsHTML(CART) +
          (giftNeedsBouquet(CART) ? needBouquetHTML() : '');
      } else {
        totals.innerHTML = totalsHTML(CART.total_price);
        var pi = totals.querySelector('.fluid-progress i');
        if (pi) pi.style.transform = 'scaleX(' + Math.min(CART.total_price / FREE_AT_MINOR, 1) + ')';
      }
    }

    cp.addEventListener('click', function (e) {
      var del = e.target.closest('[data-del]');
      if (del) { change(del.getAttribute('data-del'), 0); return; }
      var q = e.target.closest('[data-k]');
      if (q) {
        var key = q.getAttribute('data-k'), d = parseInt(q.getAttribute('data-d'), 10);
        for (var i = 0; i < CART.items.length; i++) {
          if (CART.items[i].key === key) { change(key, Math.max(0, CART.items[i].quantity + d)); return; }
        }
      }
    });

    var cpBusy = false;
    function change(key, quantity) {
      if (cpBusy) return;   /* one write at a time - see the bag's gate */
      cpBusy = true;
      fetch('/cart/change.js', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity: quantity })
      }).then(function (r) { return r.json(); }).then(function (c) {
        cpBusy = false;
        CART = c; setCount(c.item_count); buzz(8); paint();
      }).catch(function () { cpBusy = false; });
    }

    refreshCart().then(function (c0) {
      return giftFeeSettle(c0).then(function (c1) {
        if (c1 && c1.items) { CART = c1; setCount(c1.item_count); }
      }).catch(function () {});
    }).then(paint).catch(function () {});
  })();

  /* ---------------- the bar knows which room it is in -------------------- */
  (function () {
    if (home) return;                      /* the home follows its own zones */
    var p = location.pathname, name = '';
    if (p.indexOf('/collections') === 0) name = 'shop';
    else if (p.indexOf('/products') === 0) name = 'shop';
    else if (p.indexOf('/search') === 0) name = 'shop';
    if (!name) return;
    document.dispatchEvent(new CustomEvent('fluidtab', { detail: { name: name } }));
  })();

  /* ---------------- the muse card: a line that points at a person ---------
     CEO approved 21 Aug: under the collection heading, where the cut
     description used to be. A different line every visit, stable within the
     visit; tap reads another. Curated lines, no apostrophes (house style). */
  (function () {
    var muse = document.querySelector('[data-muse]');
    if (!muse) return;
    var txt = muse.querySelector('[data-musetext]');
    if (!txt) return;
    /* the lines below are English; on /ar the Liquid already painted the one
       translated line, so leave it standing and do not rotate (the tap hint
       is not rendered there either - see fluid-collection.liquid) */
    if ((document.documentElement.lang || '').indexOf('ar') === 0) return;
    var LINES = [
      'Somebody crossed your mind just now. Flowers say it better than a text.',
      'The best time to send flowers is before there is a reason.',
      'Someone you love is having an ordinary day. You could change that.',
      'You have thought of them three times today. That is the sign.',
      'Nobody ever kept a text on the kitchen table for a week.',
      'Missing someone is a message. Flowers deliver it.',
      'Say thank you the way it stays said.',
      'A just-because bouquet is remembered longer than a birthday one.',
      'Some feelings do not fit in words. They fit in a bouquet.',
      'Your mother has a favourite flower. Do you know it?'
    ];
    var ix = -1;
    try { ix = parseInt(sessionStorage.getItem('flmuse') || '-1', 10); } catch (err) {}
    if (isNaN(ix) || ix < 0 || ix >= LINES.length) {
      ix = Math.floor(Date.now() / 60000) % LINES.length;
      try { sessionStorage.setItem('flmuse', String(ix)); } catch (err) {}
    }
    txt.textContent = LINES[ix];
    muse.addEventListener('click', function () {
      buzz(6);
      ix = (ix + 1) % LINES.length;
      try { sessionStorage.setItem('flmuse', String(ix)); } catch (err) {}
      if (REDUCE.matches) { txt.textContent = LINES[ix]; return; }
      txt.style.opacity = '0';
      setTimeout(function () { txt.textContent = LINES[ix]; txt.style.opacity = ''; }, 150);
    });
  })();

  /* feel dials moved to the console: FLUID.slow(true), FLUID.bounce(true) */
  window.FLUID = {
    slow: function (on) { TIME = on ? 0.12 : 1; return TIME; },
    bounce: function (on) { BOUNCE = !!on; return BOUNCE; }
  };
})();
